import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/DashboardLayout";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { getTeachers } from "@/api/admin/users";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  addMockTestQuestion,
  assignMockTestTeacher,
  createMockTestScenario,
  deleteMockTestScenario,
  deleteMockTestQuestion,
  getMockTest,
  getMockTestQuestions,
  getMockTestScenarios,
  patchMockTestQuestion,
  patchMockTestScenario,
  updateMockTest,
  regradeAttempts,
  type RegradeResult,
  type MockTest,
  type MockTestQuestion,
} from "@/api/lms/mock-tests";
import { examConfigApi } from "@/api/admin/examConfig";
import { QuestionTypeBackend } from "@/api/lms/exams";
import type { QuestionScenario } from "@/api/lms/exams";
import {
  createEmptyMcqOptions,
  parseMcqOptionsFromBackend,
  serializeMcqOptionsForApi,
  newMcqOptionId,
  type McqOptionRow,
  mcqDisplayBadge,
} from "@/lib/mcqOptionIds";
import {
  formatMockTestLevelsForDisplay,
  JLPT_LEVEL_CODES,
  joinMockTestLevels,
  parseMockTestLevelField,
} from "@/lib/mockTestLevels";
import { uploadToS3 } from "@/api/s3";
import { renderFurigana, TextFormattingToolbar, insertFormatting } from "@/lib/japaneseHelpers";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  Plus,
  Trash2,
  Save,
  Send,
  Loader2,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  Edit3,
  Eye,
  AudioLines,
  BookOpen,
  Volume2,
  CheckCircle2,
  Image as ImageIcon,
  Copy,
  Layers,
  Clock,
  FileText,
  Settings,
  Users,
  AlertCircle,
  Languages,
} from "lucide-react";


// ─── Shared Components ────────────────────────────────────────────────────────
const SettingRow = ({ title, description, children, border = true }: { title: string, description: string, children: React.ReactNode, border?: boolean }) => (
  <div className={`flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6 py-5 ${border ? 'border-b border-border/40' : ''}`}>
    <div className="sm:w-1/3 shrink-0 space-y-1">
      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
    </div>
    <div className="sm:w-2/3 flex-1 flex flex-col justify-center">
      {children}
    </div>
  </div>
);

// ─── Types ────────────────────────────────────────────────────────────────────

type MockSection = string;

// Section-level timing (JLPT/JFT official exam structure)
type SectionTimings = Record<string, number>;

const DEFAULT_SECTION_TIMINGS: SectionTimings = {};

const SECTION_TIMING_STORAGE_KEY = (mockTestId: string) =>
  `mock_timings_${mockTestId}`;

interface UiQuestion {
  id: string;
  questionText: string;
  options: McqOptionRow[];
  correctId: string;
  marks: number;
  scenarioId: string;
  imageUrl: string;
  audioUrl: string;
  sortOrder: number;
  examCategory: MockSection;
  optionType?: "TEXT" | "IMAGE" | "MIXED";
  answerExplanation?: string;
}

type UnifiedFlowItem =
  | { type: "scenario"; id: string; sortOrder: number; scenario: QuestionScenario }
  | { type: "existing-standalone"; id: string; sortOrder: number; question: UiQuestion }
  | { type: "draft-standalone"; id: string; sortOrder: number; question: UiQuestion };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function emptyUiQuestion(category: string = ""): UiQuestion {
  const options = createEmptyMcqOptions();
  return {
    id: crypto.randomUUID(),
    questionText: "",
    options,
    correctId: options[0]!.id,
    marks: 1,
    scenarioId: "",
    imageUrl: "",
    audioUrl: "",
    sortOrder: 0,
    examCategory: category,
    optionType: "TEXT",
    answerExplanation: "",
  };
}

function getPlayLimitFromUrl(url: string | null): string {
  if (!url) return "";
  try {
    const match = url.match(/[?&]playLimit=([^&]+)/);
    return match ? match[1] : "";
  } catch {
    return "";
  }
}

function setPlayLimitInUrl(url: string | null, limit: string): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (limit) {
      u.searchParams.set("playLimit", limit);
    } else {
      u.searchParams.delete("playLimit");
    }
    return u.toString();
  } catch {
    const base = url.split('?')[0];
    return limit ? `${base}?playLimit=${limit}` : base;
  }
}


function mapMockToUiQuestion(q: MockTestQuestion): UiQuestion {
  const optsRaw = (q.options as { options?: unknown })?.options;
  const options = parseMcqOptionsFromBackend(optsRaw ?? []);
  const correctId =
    options.find((o) => o.text?.trim() === q.correctAnswer?.trim())?.id ?? options[0]?.id ?? "";
  const metadata = (q.options as { metadata?: any })?.metadata || {};
  return {
    id: q.id,
    questionText: q.questionText,
    options,
    correctId,
    marks: q.marks ?? 1,
    scenarioId: q.scenarioId ?? "",
    imageUrl: q.imageUrl ?? "",
    audioUrl: q.audioUrl ?? "",
    sortOrder: q.sortOrder ?? 0,
    examCategory: metadata.examCategory || "",
    optionType: metadata.optionType || "TEXT",
    answerExplanation: metadata.answerExplanation ?? q.answerExplanation ?? "",
  };
}

const CIRCLE_BADGES = ["①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧", "⑨", "⑩"];
function getCircleBadge(index: number) {
  return CIRCLE_BADGES[index] || String(index + 1);
}



// ─── Component ────────────────────────────────────────────────────────────────

export default function MockTestEditorPage() {
  const { mockTestId = "" } = useParams<{ mockTestId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const isAdmin = user?.role === "admin";

  // State
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>("ALL");
  const [collapsedScenarios, setCollapsedScenarios] = useState<Record<string, boolean>>({});
  const [draftQuestions, setDraftQuestions] = useState<UiQuestion[]>([]);
  const [existingEditUi, setExistingEditUi] = useState<UiQuestion[]>([]);
  const [savingQuestionId, setSavingQuestionId] = useState<string | null>(null);
  const [scenarioMediaSavingId, setScenarioMediaSavingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [editingScenarioId, setEditingScenarioId] = useState<string | null>(null);
  const [editingScenarioText, setEditingScenarioText] = useState("");

  // Re-grade dialog state
  const [showRegradeDialog, setShowRegradeDialog] = useState(false);
  const [regradePreview, setRegradePreview] = useState<RegradeResult | null>(null);
  const [regradeLoading, setRegradeLoading] = useState(false);
  const [regradeApplying, setRegradeApplying] = useState(false);

  // Config form state
  const [editTitle, setEditTitle] = useState("");
  const [editPosition, setEditPosition] = useState(1);
  const [editDuration, setEditDuration] = useState(60);
  const [editLevels, setEditLevels] = useState<string[]>(["N5"]);
  const [teacherIdToAssign, setTeacherIdToAssign] = useState<number | "">("");
  const [editIsPublished, setEditIsPublished] = useState(false);
  const [editPassingMarks, setEditPassingMarks] = useState(0);
  const [editCategoryPassMarks, setEditCategoryPassMarks] = useState<Record<string, number>>({});

  // Pricing & Access form state
  const [editPrice, setEditPrice] = useState(0);
  const [editAttemptLimit, setEditAttemptLimit] = useState(1);
  const [editBundleEligible, setEditBundleEligible] = useState(true);
  const [editAccessDays, setEditAccessDays] = useState<number | "">("")

  // Section timing state (JLPT/JFT official section structure)
  const [sectionTimings, setSectionTimings] = useState<SectionTimings>(DEFAULT_SECTION_TIMINGS);

  const questionStemRef = useRef<HTMLTextAreaElement>(null);
  const scenarioPassageRef = useRef<HTMLTextAreaElement>(null);

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: activeConfigs = [] } = useQuery({
    queryKey: isAdmin ? ["allExamConfigs"] : ["activeExamConfigs"],
    queryFn: () => isAdmin ? examConfigApi.getAll() : examConfigApi.getActive(),
  });

  const { data: mockTest, isLoading: metaLoading, error: metaError } = useQuery({
    queryKey: ["mockTest", mockTestId],
    queryFn: () => getMockTest(mockTestId),
    enabled: !!mockTestId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 10,
  });

  const activeConfig = activeConfigs.find(c => c.code === mockTest?.examType);
  const dynamicSections = activeConfig?.sections || [];

  const { data: selectedQuestions = [], isLoading: questionsLoading, error: questionsError } = useQuery({
    queryKey: ["mockQuestions", mockTestId],
    queryFn: () => getMockTestQuestions(mockTestId),
    enabled: !!mockTestId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 10,
  });

  const { data: mockScenarios = [], isLoading: scenariosLoading } = useQuery({
    queryKey: ["mockScenarios", mockTestId],
    queryFn: () => getMockTestScenarios(mockTestId),
    enabled: !!mockTestId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 10,
  });

  const { data: teacherOptions = [] } = useQuery({
    queryKey: ["adminTeachers"],
    queryFn: () => getTeachers(),
    enabled: isAdmin,
  });

  const getSectionMeta = useCallback((sec: string) => {
    const configSection = dynamicSections.find(s => s.code === sec);
    if (!configSection) return { label: sec || "Unknown", color: "text-gray-600", bg: "bg-gray-500/10", jp: sec || "Unknown" };
    return {
      label: configSection.englishName || configSection.name,
      jp: configSection.japaneseName || configSection.name,
      color: "text-primary",
      bg: "bg-primary/10"
    };
  }, [dynamicSections]);

  // ── Sync state ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (mockTest) {
      setEditTitle(mockTest.title ?? "");
      setEditDuration(mockTest.duration ?? 60);
      const parsed = parseMockTestLevelField(mockTest.level);
      setEditLevels(parsed.length ? parsed : ["N5"]);
      setEditIsPublished(mockTest.isPublished ?? false);

      if (mockTest.sectionDurations) {
        try {
          const parsed = JSON.parse(mockTest.sectionDurations) as SectionTimings;
          setSectionTimings({ ...DEFAULT_SECTION_TIMINGS, ...parsed });
        } catch {
          setSectionTimings({ ...DEFAULT_SECTION_TIMINGS });
        }
      } else {
        // Load section timings from localStorage as fallback
        try {
          const stored = localStorage.getItem(SECTION_TIMING_STORAGE_KEY(mockTest.id));
          if (stored) {
            const parsed = JSON.parse(stored) as SectionTimings;
            setSectionTimings({ ...DEFAULT_SECTION_TIMINGS, ...parsed });
          } else {
            setSectionTimings({ ...DEFAULT_SECTION_TIMINGS });
          }
        } catch {
          setSectionTimings({ ...DEFAULT_SECTION_TIMINGS });
        }
      }

      setEditPassingMarks(mockTest.passingMarks ?? 0);
      if (mockTest.categoryPassMarks) {
        setEditCategoryPassMarks(mockTest.categoryPassMarks as Record<string, number> || {});
      } else {
        setEditCategoryPassMarks({});
      }

      // Pricing & Access sync
      setEditPrice(Number(mockTest.price ?? 0));
      setEditAttemptLimit(mockTest.attemptLimit ?? 1);
      setEditBundleEligible(mockTest.bundleEligible ?? true);
      setEditAccessDays(mockTest.accessDays ?? "");
    }
  }, [mockTest]);

  useEffect(() => {
    if (selectedQuestions.length > 0) {
      setExistingEditUi(prev => {
        const prevMap = new Map(prev.map(p => [p.id, p]));
        return selectedQuestions.map(serverQ => {
          return prevMap.has(serverQ.id) ? prevMap.get(serverQ.id)! : mapMockToUiQuestion(serverQ);
        });
      });
    } else {
      setExistingEditUi([]);
    }
  }, [selectedQuestions]);

  // Helper to compare questions
  const isQuestionEqual = useCallback((q1: UiQuestion, q2: UiQuestion) => {
    if (q1.id !== q2.id) return false;
    if (q1.questionText !== q2.questionText) return false;
    if (q1.correctId !== q2.correctId) return false;
    if (q1.marks !== q2.marks) return false;
    if (q1.scenarioId !== q2.scenarioId) return false;
    if (q1.imageUrl !== q2.imageUrl) return false;
    if (q1.audioUrl !== q2.audioUrl) return false;
    if (q1.examCategory !== q2.examCategory) return false;
    if (q1.options.length !== q2.options.length) return false;
    for (let i = 0; i < q1.options.length; i++) {
      if (q1.options[i].text !== q2.options[i].text) return false;
    }
    return true;
  }, []);

  const isDirty = useMemo(() => {
    // 1. Check if there are draft questions
    if (draftQuestions.length > 0) return true;

    // 2. Check if settings/meta forms are modified compared to database
    if (mockTest) {
      if (editTitle !== (mockTest.title ?? "")) return true;
      if (editPosition !== (mockTest.position ?? 1)) return true;
      if (editDuration !== (mockTest.duration ?? 60)) return true;
      if (editPassingMarks !== (mockTest.passingMarks ?? 0)) return true;

      const cleanLevels = parseMockTestLevelField(mockTest.level);
      const sortedEdit = [...editLevels].sort();
      const sortedClean = [...(cleanLevels.length ? cleanLevels : ["N5"])].sort();
      if (JSON.stringify(sortedEdit) !== JSON.stringify(sortedClean)) return true;

      const cleanCategoryPass = mockTest.categoryPassMarks || { VOCABULARY: 0, GRAMMAR: 0, READING: 0, LISTENING: 0 };
      if (
        editCategoryPassMarks.VOCABULARY !== (cleanCategoryPass.VOCABULARY ?? 0) ||
        editCategoryPassMarks.GRAMMAR !== (cleanCategoryPass.GRAMMAR ?? 0) ||
        editCategoryPassMarks.READING !== (cleanCategoryPass.READING ?? 0) ||
        editCategoryPassMarks.LISTENING !== (cleanCategoryPass.LISTENING ?? 0)
      ) return true;

      let cleanDurations: SectionTimings = { ...DEFAULT_SECTION_TIMINGS };
      if (mockTest.sectionDurations) {
        try { cleanDurations = { ...cleanDurations, ...JSON.parse(mockTest.sectionDurations) }; } catch { }
      }
      if (JSON.stringify(sectionTimings) !== JSON.stringify(cleanDurations)) return true;
    }

    // 3. Check if any question in existingEditUi has been modified compared to selectedQuestions
    if (existingEditUi.length !== selectedQuestions.length) return true;
    for (let i = 0; i < existingEditUi.length; i++) {
      const qUi = existingEditUi[i];
      const qClean = selectedQuestions.find(q => q.id === qUi.id);
      if (!qClean) return true;
      const qCleanUi = mapMockToUiQuestion(qClean);
      if (!isQuestionEqual(qUi, qCleanUi)) return true;
    }

    // 4. Check if there is scenario passage being edited
    if (editingScenarioId && editingScenarioText.trim() !== "") {
      const scen = mockScenarios.find(s => s.id === editingScenarioId);
      if (scen && editingScenarioText.trim() !== (scen.instructionsText ?? "").trim()) {
        return true;
      }
    }

    return false;
  }, [
    mockTest,
    draftQuestions,
    existingEditUi,
    selectedQuestions,
    editTitle,
    editPosition,
    editDuration,
    editPassingMarks,
    editLevels,
    editCategoryPassMarks,
    sectionTimings,
    editingScenarioId,
    editingScenarioText,
    mockScenarios,
    isQuestionEqual
  ]);

  // Unsaved changes beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  // Debounced localStorage autosave
  useEffect(() => {
    if (!mockTestId) return;

    if (isDirty) {
      const timer = setTimeout(() => {
        const snapshot = {
          mockTestId,
          timestamp: Date.now(),
          draftQuestions,
          existingEditUi,
          editingScenarioId,
          editingScenarioText,
          activeItemId,
          activeSection,
          editTitle,
          editPosition,
          editDuration,
          editLevels,
          editIsPublished,
          editPassingMarks,
          editCategoryPassMarks,
          sectionTimings,
        };
        localStorage.setItem(`mock_test_editor_draft_${mockTestId}`, JSON.stringify(snapshot));
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      localStorage.removeItem(`mock_test_editor_draft_${mockTestId}`);
    }
  }, [
    isDirty,
    mockTestId,
    draftQuestions,
    existingEditUi,
    editingScenarioId,
    editingScenarioText,
    activeItemId,
    activeSection,
    editTitle,
    editPosition,
    editDuration,
    editLevels,
    editIsPublished,
    editPassingMarks,
    editCategoryPassMarks,
    sectionTimings,
  ]);

  const [recoveredDraft, setRecoveredDraft] = useState<any>(null);
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);

  // Check for unsaved draft on load
  useEffect(() => {
    if (mockTestId && !metaLoading && !questionsLoading) {
      const stored = localStorage.getItem(`mock_test_editor_draft_${mockTestId}`);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.timestamp) {
            setRecoveredDraft(parsed);
            setShowRecoveryDialog(true);
          }
        } catch (err) {
          console.error("Failed to parse editor draft:", err);
        }
      }
    }
  }, [mockTestId, metaLoading, questionsLoading]);

  const handleRecoverDraft = () => {
    if (!recoveredDraft) return;
    setDraftQuestions(recoveredDraft.draftQuestions || []);
    setExistingEditUi(recoveredDraft.existingEditUi || []);
    setEditingScenarioId(recoveredDraft.editingScenarioId || null);
    setEditingScenarioText(recoveredDraft.editingScenarioText || "");
    setActiveItemId(recoveredDraft.activeItemId || null);
    setActiveSection(recoveredDraft.activeSection || "ALL");
    setEditTitle(recoveredDraft.editTitle || "");
    setEditPosition(recoveredDraft.editPosition || 1);
    setEditDuration(recoveredDraft.editDuration || 60);
    setEditLevels(recoveredDraft.editLevels || ["N5"]);
    setEditIsPublished(recoveredDraft.editIsPublished || false);
    setEditPassingMarks(recoveredDraft.editPassingMarks || 0);
    setEditCategoryPassMarks(recoveredDraft.editCategoryPassMarks || {});
    setSectionTimings(recoveredDraft.sectionTimings || { ...DEFAULT_SECTION_TIMINGS });

    setShowRecoveryDialog(false);
    toast.success("Unsaved draft restored successfully!");
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem(`mock_test_editor_draft_${mockTestId}`);
    setRecoveredDraft(null);
    setShowRecoveryDialog(false);
    toast.info("Draft discarded.");
  };

  // Auto-select first item
  useEffect(() => {
    if (!activeItemId && unifiedFlow.length > 0) {
      setActiveItemId(unifiedFlow[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────

  const loadError = useMemo(() => {
    if (metaError) return (metaError as any).message || "Mock test not found";
    if (questionsError) return "You do not have permission to edit this mock test.";
    return null;
  }, [metaError, questionsError]);

  const unifiedFlow = useMemo<UnifiedFlowItem[]>(() => {
    const list: UnifiedFlowItem[] = [];

    for (const s of mockScenarios) {
      const subQs = [...existingEditUi, ...draftQuestions].filter(q => q.scenarioId === s.id);
      if (activeSection === "ALL") {
        list.push({ type: "scenario", id: s.id, sortOrder: s.sortOrder ?? 0, scenario: s });
      } else {
        const hasCategoryMatch = subQs.some(q => q.examCategory === activeSection);
        if (subQs.length === 0 || hasCategoryMatch) {
          list.push({ type: "scenario", id: s.id, sortOrder: s.sortOrder ?? 0, scenario: s });
        }
      }
    }

    const existingStandalone = existingEditUi.filter(
      q => !q.scenarioId && (activeSection === "ALL" || q.examCategory === activeSection)
    );
    for (const q of existingStandalone) {
      list.push({ type: "existing-standalone", id: q.id, sortOrder: q.sortOrder ?? 0, question: q });
    }

    const draftStandalone = draftQuestions.filter(
      q => !q.scenarioId && (activeSection === "ALL" || q.examCategory === activeSection)
    );
    for (const q of draftStandalone) {
      list.push({ type: "draft-standalone", id: q.id, sortOrder: q.sortOrder ?? 0, question: q });
    }

    list.sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.id.localeCompare(b.id);
    });

    return list;
  }, [mockScenarios, existingEditUi, draftQuestions, activeSection]);

  const activeQuestion = useMemo(() => {
    return existingEditUi.find(o => o.id === activeItemId) ||
      draftQuestions.find(o => o.id === activeItemId) ||
      null;
  }, [activeItemId, existingEditUi, draftQuestions]);

  const activeScenario = useMemo(() => {
    return mockScenarios.find(o => o.id === activeItemId) || null;
  }, [activeItemId, mockScenarios]);

  const totalMarks = useMemo(() => {
    return [...existingEditUi, ...draftQuestions].reduce((s, q) => s + (q.marks ?? 1), 0);
  }, [existingEditUi, draftQuestions]);

  const targetTotalMarks = mockTest?.totalMarks || 180;

  const computedCategoryTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    [...existingEditUi, ...draftQuestions].forEach(q => {
      const cat = q.examCategory || "";
      totals[cat] = (totals[cat] || 0) + (q.marks ?? 1);
    });
    return totals;
  }, [existingEditUi, draftQuestions]);

  const totalQuestions = existingEditUi.length + draftQuestions.length;

  // ── Mutations ──────────────────────────────────────────────────────────────

  const updateMetaMutation = useMutation({
    mutationFn: (data: any) => updateMockTest(mockTestId, data),
    onSuccess: (_data, variables) => {
      if ('isPublished' in variables) {
        const published = variables.isPublished;
        toast.success(published ? "Test is now Live • visible to students" : "Test moved back to Draft");
      } else {
        toast.success("Settings saved");
      }
      queryClient.invalidateQueries({ queryKey: ["mockTest", mockTestId] });
    },
    onError: (err: any) => toast.error(err?.message || "Save failed"),
  });

  // Dedicated mutation only for toggling publish state — never bundled with settings
  const setPublishedMutation = useMutation({
    mutationFn: (publish: boolean) => updateMockTest(mockTestId, { isPublished: publish }),
    onSuccess: (_data, publish) => {
      toast.success(publish ? "✅ Test is now Live • visible to students" : "📝 Test moved back to Draft");
      queryClient.invalidateQueries({ queryKey: ["mockTest", mockTestId] });
    },
    onError: (err: any) => toast.error(err?.message || "Failed to update visibility"),
  });

  const assignTeacherMutation = useMutation({
    mutationFn: (teacherId: number) => assignMockTestTeacher(mockTestId, teacherId),
    onSuccess: () => {
      toast.success("Teacher assigned");
      setTeacherIdToAssign("");
    },
    onError: (err: any) => toast.error(err?.message || "Assign failed"),
  });

  const addScenarioMutation = useMutation({
    mutationFn: () => {
      const sortOrder = unifiedFlow.length > 0
        ? Math.max(...unifiedFlow.map(f => f.sortOrder)) + 1
        : 1;
      return createMockTestScenario(mockTestId, { sortOrder });
    },
    onSuccess: (s) => {
      toast.success("Scenario block added");
      queryClient.invalidateQueries({ queryKey: ["mockScenarios", mockTestId] });
      setActiveItemId(s.id);
    },
    onError: (err: any) => toast.error(err?.message || "Failed to add block"),
  });

  const deleteScenarioMutation = useMutation({
    mutationFn: (scenarioId: string) => deleteMockTestScenario(mockTestId, scenarioId),
    onSuccess: () => {
      toast.success("Scenario deleted");
      setActiveItemId(null);
      queryClient.invalidateQueries({ queryKey: ["mockScenarios", mockTestId] });
      queryClient.invalidateQueries({ queryKey: ["mockQuestions", mockTestId] });
    },
    onError: (err: any) => toast.error(err?.message || "Could not delete scenario"),
  });

  const deleteQuestionMutation = useMutation({
    mutationFn: (qId: string) => deleteMockTestQuestion(mockTestId, qId),
    onSuccess: () => {
      toast.success("Question deleted");
      setActiveItemId(null);
      queryClient.invalidateQueries({ queryKey: ["mockQuestions", mockTestId] });
      queryClient.invalidateQueries({ queryKey: ["mockTest", mockTestId] });
    },
    onError: (err: any) => toast.error(err?.message || "Could not delete question"),
  });

  const saveQuestionMutation = useMutation({
    mutationFn: (ui: UiQuestion) => {
      const raw = selectedQuestions.find((q) => q.id === ui.id);
      if (!raw) throw new Error("Question not found");
      const payload: any = {
        type: raw.type,
        questionText: ui.questionText.trim(),
        marks: ui.marks,
        scenarioId: ui.scenarioId || null,
        imageUrl: ui.imageUrl.trim() || null,
        audioUrl: ui.audioUrl.trim() || null,
      };

      const metadata = {
        examCategory: ui.examCategory,
        optionType: ui.optionType || "TEXT",
        answerExplanation: ui.answerExplanation?.trim() || null,
      };

      // Guard: section/category must always be saved — its absence causes all questions
      // to fall into the same section on the student side.
      if (!ui.examCategory || ui.examCategory.trim() === "") {
        throw new Error("Please select a section (examCategory) for this question before saving.");
      }

      if (raw.type === QuestionTypeBackend.mcq) {
        const optionType = ui.optionType || "TEXT";
        const finalOptions = ui.options.map((opt, idx) => {
          if (optionType === "IMAGE") {
            return { ...opt, text: mcqDisplayBadge(idx) };
          }
          return opt;
        });

        // Validation by layout type
        if (optionType === "TEXT" && finalOptions.some(o => !o.text.trim())) {
          throw new Error("Please enter text for all options");
        }
        if (optionType === "IMAGE" && finalOptions.some(o => !o.imageUrl.trim())) {
          throw new Error("Please upload image for all options");
        }
        if (optionType === "MIXED" && finalOptions.some(o => !o.text.trim() || !o.imageUrl.trim())) {
          throw new Error("Please complete both text and image for all options");
        }

        const optionTexts = finalOptions.map(o => o.text.trim()).filter(Boolean);
        if (optionTexts.length < 2) throw new Error("At least 2 options required");
        const correct = finalOptions.find(o => o.id === ui.correctId)?.text.trim();
        if (!correct) throw new Error("Select the correct option");

        payload.options = {
          options: serializeMcqOptionsForApi(finalOptions).options,
          metadata,
        };
        payload.correctAnswer = correct;
      } else {
        payload.options = {
          options: [],
          metadata,
        };
      }
      return patchMockTestQuestion(mockTestId, ui.id, payload);
    },
    onMutate: (ui) => setSavingQuestionId(ui.id),
    onSuccess: () => {
      toast.success("Question saved");
      queryClient.invalidateQueries({ queryKey: ["mockQuestions", mockTestId] });
    },
    onError: (err: any) => toast.error(err?.message || "Save failed"),
    onSettled: () => setSavingQuestionId(null),
  });

  const publishDraftsMutation = useMutation({
    mutationFn: async () => {
      const toAdd = orderedDraftsWithText();
      if (toAdd.length === 0) throw new Error("Add at least one question with text");
      let sort = selectedQuestions.length > 0
        ? Math.max(...selectedQuestions.map(q => q.sortOrder ?? 0)) + 1
        : 1;
      for (const q of toAdd) {
        const optionType = q.optionType || "TEXT";
        const finalOptions = q.options.map((opt, idx) => {
          if (optionType === "IMAGE") {
            return { ...opt, text: mcqDisplayBadge(idx) };
          }
          return opt;
        });

        // Validation by layout type
        if (optionType === "TEXT" && finalOptions.some(o => !o.text.trim())) {
          throw new Error(`Please enter text for all options in Question "${q.questionText}"`);
        }
        if (optionType === "IMAGE" && finalOptions.some(o => !o.imageUrl.trim())) {
          throw new Error(`Please upload image for all options in Question "${q.questionText}"`);
        }
        if (optionType === "MIXED" && finalOptions.some(o => !o.text.trim() || !o.imageUrl.trim())) {
          throw new Error(`Please complete both text and image for all options in Question "${q.questionText}"`);
        }

        const optionTexts = finalOptions.map(o => o.text.trim()).filter(Boolean);
        if (optionTexts.length < 2) throw new Error("Each question needs at least 2 options");
        const correct = finalOptions.find(o => o.id === q.correctId)?.text.trim();
        if (!correct) throw new Error("Select the correct option for each question");
        if (!q.examCategory || q.examCategory.trim() === "") {
          throw new Error(`Please select a section for Question "${q.questionText || "(empty)"}".`);
        }
        await addMockTestQuestion(mockTestId, {
          type: "MCQ",
          questionText: q.questionText.trim(),
          options: {
            options: serializeMcqOptionsForApi(finalOptions).options,
            metadata: {
              examCategory: q.examCategory,
              optionType: optionType,
              answerExplanation: q.answerExplanation?.trim() || undefined,
            },
          },
          correctAnswer: correct,
          marks: q.marks,
          scenarioId: q.scenarioId || undefined,
          sortOrder: sort,
          imageUrl: q.imageUrl || undefined,
          audioUrl: q.audioUrl || undefined,
        });
        sort += 1;
      }
    },
    onSuccess: () => {
      toast.success("Questions published successfully");
      setDraftQuestions([]);
      setActiveItemId(null);
      queryClient.invalidateQueries({ queryKey: ["mockQuestions", mockTestId] });
      queryClient.invalidateQueries({ queryKey: ["mockTest", mockTestId] });
    },
    onError: (err: any) => toast.error(err?.message || "Publish failed"),
  });

  const updateScenarioMediaMutation = useMutation({
    mutationFn: ({ scenarioId, data }: { scenarioId: string; data: any }) =>
      patchMockTestScenario(mockTestId, scenarioId, data),
    onMutate: ({ scenarioId }) => setScenarioMediaSavingId(scenarioId),
    onSuccess: () => {
      toast.success("Saved");
      queryClient.invalidateQueries({ queryKey: ["mockScenarios", mockTestId] });
    },
    onError: (err: any) => toast.error(err?.message || "Save failed"),
    onSettled: () => setScenarioMediaSavingId(null),
  });

  // ── Handlers ───────────────────────────────────────────────────────────────

  function updateActiveQuestion(updater: (prev: UiQuestion) => UiQuestion) {
    if (!activeQuestion) return;
    if (existingEditUi.some(q => q.id === activeQuestion.id)) {
      setExistingEditUi(prev => prev.map(x => x.id === activeQuestion.id ? updater(x) : x));
    } else {
      setDraftQuestions(prev => prev.map(x => x.id === activeQuestion.id ? updater(x) : x));
    }
  }

  function moveOption(idx: number, direction: "up" | "down") {
    updateActiveQuestion(q => {
      const nextOptions = [...q.options];
      const targetIdx = direction === "up" ? idx - 1 : idx + 1;
      if (targetIdx >= 0 && targetIdx < nextOptions.length) {
        const temp = nextOptions[idx]!;
        nextOptions[idx] = nextOptions[targetIdx]!;
        nextOptions[targetIdx] = temp;
      }
      return { ...q, options: nextOptions };
    });
  }

  function orderedDraftsWithText(): UiQuestion[] {
    const ordered: UiQuestion[] = [];
    for (const s of mockScenarios) {
      for (const d of draftQuestions) {
        if (d.questionText.trim() && d.scenarioId === s.id) ordered.push(d);
      }
    }
    for (const d of draftQuestions) {
      if (d.questionText.trim() && !d.scenarioId) ordered.push(d);
    }
    return ordered;
  }

  function handleDuplicateQuestion(q: UiQuestion) {
    let newCorrectId = q.correctId;
    const options = q.options.map(o => {
      const newId = newMcqOptionId();
      if (o.id === q.correctId) newCorrectId = newId;
      return { ...o, id: newId };
    });
    const duplicated: UiQuestion = {
      ...q,
      id: crypto.randomUUID(),
      sortOrder: (q.sortOrder ?? 0) + 1,
      questionText: q.questionText ? `${q.questionText} (Copy)` : "Duplicated Question",
      options,
      correctId: newCorrectId,
    };
    setDraftQuestions(prev => [...prev, duplicated]);
    setActiveItemId(duplicated.id);
    toast.success("Duplicated as draft");
  }

  function handleDeleteQuestion(qId: string, isDraft: boolean) {
    if (isDraft) {
      setDraftQuestions(prev => prev.filter(x => x.id !== qId));
      if (activeItemId === qId) setActiveItemId(null);
      toast.success("Draft removed");
    } else {
      setDeleteConfirmId(qId);
    }
  }

  const handleReorder = async (index: number, direction: "up" | "down") => {
    const targetIdx = direction === "up" ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= unifiedFlow.length) return;

    const nextFlow = [...unifiedFlow];
    const temp = nextFlow[index]!;
    nextFlow[index] = nextFlow[targetIdx]!;
    nextFlow[targetIdx] = temp;

    const apiCalls: Promise<any>[] = [];
    const nextDrafts = [...draftQuestions];

    nextFlow.forEach((node, flowIdx) => {
      const newSort = flowIdx + 1;
      if (node.type === "draft-standalone") {
        const idx = nextDrafts.findIndex(d => d.id === node.id);
        if (idx !== -1) nextDrafts[idx] = { ...nextDrafts[idx]!, sortOrder: newSort };
      } else if (node.type === "existing-standalone" && node.question.sortOrder !== newSort) {
        apiCalls.push(patchMockTestQuestion(mockTestId, node.id, { sortOrder: newSort }));
      } else if (node.type === "scenario" && node.scenario.sortOrder !== newSort) {
        apiCalls.push(patchMockTestScenario(mockTestId, node.id, { sortOrder: newSort }));
      }
    });

    setDraftQuestions(nextDrafts);
    if (apiCalls.length > 0) {
      try {
        await Promise.all(apiCalls);
        toast.success("Reordered");
        queryClient.invalidateQueries({ queryKey: ["mockQuestions", mockTestId] });
        queryClient.invalidateQueries({ queryKey: ["mockScenarios", mockTestId] });
      } catch {
        toast.error("Failed to reorder");
      }
    } else {
      toast.success("Drafts reordered");
    }
  };

  const handleMoveSubQuestion = async (qId: string, direction: "up" | "down", scenarioId: string) => {
    const isDraft = draftQuestions.some(d => d.id === qId);
    if (isDraft) {
      setDraftQuestions(prev => {
        const indices = prev.map((q, i) => (q.scenarioId === scenarioId ? i : -1)).filter(i => i !== -1);
        const subList = indices.map(i => prev[i]!);
        const curr = subList.findIndex(q => q.id === qId);
        const target = direction === "up" ? curr - 1 : curr + 1;
        if (target < 0 || target >= subList.length) return prev;
        const ri1 = indices[curr]!;
        const ri2 = indices[target]!;
        const next = [...prev];
        const t = next[ri1]!;
        next[ri1] = next[ri2]!;
        next[ri2] = t;
        const s1 = next[ri1]!.sortOrder;
        const s2 = next[ri2]!.sortOrder;
        next[ri1]!.sortOrder = s2;
        next[ri2]!.sortOrder = s1;
        return next;
      });
    } else {
      const list = existingEditUi.filter(q => q.scenarioId === scenarioId).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      const idx = list.findIndex(q => q.id === qId);
      const targetIdx = direction === "up" ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= list.length) return;
      const q1 = list[idx]!;
      const q2 = list[targetIdx]!;
      try {
        await Promise.all([
          patchMockTestQuestion(mockTestId, q1.id, { sortOrder: q2.sortOrder ?? targetIdx }),
          patchMockTestQuestion(mockTestId, q2.id, { sortOrder: q1.sortOrder ?? idx }),
        ]);
        queryClient.invalidateQueries({ queryKey: ["mockQuestions", mockTestId] });
        toast.success("Reordered");
      } catch {
        toast.error("Failed to reorder");
      }
    }
  };

  function toggleLevel(lvl: string) {
    setEditLevels(prev => {
      if (prev.includes(lvl)) {
        const next = prev.filter(x => x !== lvl);
        return next.length ? next : prev;
      }
      return [...prev, lvl];
    });
  }

  // ── Guards ─────────────────────────────────────────────────────────────────

  if (!user || user.role === "student") return null;

  const isLoading = metaLoading || questionsLoading || scenariosLoading;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="p-10 flex flex-col justify-center items-center gap-3 text-muted-foreground min-h-[500px]">
          <Loader2 className="animate-spin text-primary" size={32} />
          <p className="text-sm font-medium animate-pulse">Loading Mock Test Composer...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (loadError && !mockTest) {
    return (
      <DashboardLayout>
        <div className="p-6 max-w-lg mx-auto mt-12">
          <div className="p-6 rounded-2xl border border-destructive/30 bg-destructive/5 text-destructive text-sm font-medium">
            {loadError}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isGlobalSaving = updateMetaMutation.isPending || publishDraftsMutation.isPending ||
    deleteScenarioMutation.isPending || assignTeacherMutation.isPending || setPublishedMutation.isPending;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <div className="w-full px-4 md:px-5 pt-4 md:pt-5 pb-2 max-w-[1680px] mx-auto flex flex-col gap-2 min-h-[calc(100vh-64px)] xl:h-[calc(100vh-64px)]">

        {/* ── Top Header Bar ───────────────────────────────────────────── */}
        <div className="dash-page-header !mb-2 pb-3 border-b border-border/40 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" className="text-muted-foreground h-8 px-2 shrink-0" asChild>
              <Link to="/mock-tests">
                <ArrowLeft className="size-4 mr-1" />
                Back
              </Link>
            </Button>
            <div className="min-w-0">
              <h1 className="dash-page-title truncate flex items-center gap-2">
                <Layers className="size-5 text-primary shrink-0" />
                {editTitle || "Mock Test Composer"}
              </h1>
              <div className="dash-page-subtitle mt-0.5 flex items-center gap-2 flex-wrap">
                <span className="flex items-center gap-1">
                  <FileText className="size-3" />
                  {formatMockTestLevelsForDisplay(mockTest?.level)}
                </span>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <Clock className="size-3" />
                  {editDuration}min
                </span>
                <span>·</span>
                <span>{selectedQuestions.length} questions</span>
                {draftQuestions.length > 0 && (
                  <>
                    <span>·</span>
                    <span className="text-amber-500 font-semibold">{draftQuestions.length} uncommitted</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Published status badge */}


            {/* Config / Settings toggle */}
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs gap-1.5"
                >
                  <Settings size={13} />
                  Config
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-2xl overflow-y-auto pb-0" side="right">
                <SheetHeader className="mb-6">
                  <SheetTitle>Mock Test Configuration</SheetTitle>
                  <SheetDescription>
                    Configure timings, pass marks, pricing, and administrative settings.
                  </SheetDescription>
                </SheetHeader>

                <div className="space-y-2">
                  <SettingRow title="Mock Test Title" description="The public title displayed to students.">
                    <Input
                      className="h-9 text-sm rounded-xl"
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                    />
                  </SettingRow>

                  <SettingRow title="Display Position" description="Order in which this test appears on the public listing.">
                    <Input
                      type="number"
                      min={1}
                      className="h-9 text-sm rounded-xl"
                      value={editPosition}
                      onChange={e => setEditPosition(Number(e.target.value))}
                    />
                  </SettingRow>

                  <SettingRow title="JLPT Level" description="Tag this test with a specific difficulty level.">
                    {activeConfig?.levels?.length === 1 ? (
                      <div className="h-9 px-4 inline-flex items-center rounded-xl border border-purple-250 bg-purple-500/10 text-purple-700 dark:border-purple-800 dark:text-purple-400 font-bold text-sm">
                        {activeConfig.levels[0].name}
                      </div>
                    ) : (
                      <div className="flex gap-2 flex-wrap">
                        {(activeConfig?.levels && activeConfig.levels.length > 0) ? (
                          activeConfig.levels.map(lvl => (
                            <button
                              key={lvl.code}
                              type="button"
                              onClick={() => toggleLevel(lvl.code)}
                              className={`h-9 px-4 rounded-xl text-sm font-bold border transition-all ${editLevels.includes(lvl.code)
                                ? "bg-primary text-primary-foreground border-primary"
                                : "border-border text-muted-foreground hover:border-primary/50"
                                }`}
                            >
                              {lvl.name}
                            </button>
                          ))
                        ) : (
                          <div className="text-sm text-muted-foreground italic mt-1">No levels configured</div>
                        )}
                      </div>
                    )}
                  </SettingRow>

                  <SettingRow title="Duration" description={activeConfig?.passCriteria === 'SECTIONAL_REQUIRED' ? "Sectional test duration is automatically computed from individual section timings." : "Total allowed time in minutes."}>
                    {activeConfig?.passCriteria !== 'SECTIONAL_REQUIRED' ? (
                      <div className="relative max-w-[150px]">
                        <Input
                          type="number"
                          min={1}
                          className="h-9 text-sm rounded-xl"
                          value={editDuration}
                          onChange={e => setEditDuration(Number(e.target.value))}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-medium pointer-events-none">min</span>
                      </div>
                    ) : (
                      <div className="h-9 px-4 max-w-[150px] flex items-center rounded-xl border border-border bg-muted/30 text-sm font-mono text-muted-foreground select-none">
                        {Object.values(sectionTimings).reduce((a, b) => a + b, 0)} min
                      </div>
                    )}
                  </SettingRow>

                  {activeConfig?.passCriteria === "SECTIONAL_REQUIRED" && (
                    <SettingRow title="Section Timings" description="Set individual time limits for each section.">
                      <div className="grid grid-cols-2 gap-4">
                        {dynamicSections.map((s) => {
                          const key = s.code;
                          const jp = s.japaneseName || s.name;
                          const label = s.englishName || s.name;
                          return (
                            <div key={key} className="space-y-1.5">
                              <Label className="text-xs font-bold text-foreground">
                                {jp} <span className="font-normal text-muted-foreground">({label})</span>
                              </Label>
                              <div className="relative">
                                <Input
                                  type="number"
                                  min={1}
                                  max={240}
                                  className="h-9 text-sm pr-12 rounded-xl"
                                  value={sectionTimings[key]}
                                  onChange={e => {
                                    const v = Math.max(1, Number(e.target.value) || 1);
                                    setSectionTimings(prev => ({ ...prev, [key]: v }));
                                  }}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground font-medium pointer-events-none">min</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </SettingRow>
                  )}

                  <SettingRow title="Overall Pass Mark" description="Minimum score student must achieve overall.">
                    <div className="space-y-2">
                      <div className="relative max-w-[150px]">
                        <Input
                          type="number"
                          min={0}
                          max={targetTotalMarks}
                          className={cn(
                            "h-9 text-sm pr-12 rounded-xl focus:ring-primary",
                            editPassingMarks > targetTotalMarks && "border-destructive focus:ring-destructive text-destructive"
                          )}
                          value={editPassingMarks}
                          onChange={e => setEditPassingMarks(Math.max(0, Number(e.target.value) || 0))}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-medium pointer-events-none">/ {targetTotalMarks}</span>
                      </div>
                      {editPassingMarks > targetTotalMarks && (
                        <p className="text-xs text-destructive font-medium">Cannot exceed total marks!</p>
                      )}
                    </div>
                  </SettingRow>

                  {activeConfig?.passCriteria === "SECTIONAL_REQUIRED" && (
                    <SettingRow title="Section Pass Marks" description="Minimum score required for each section.">
                      <div className="grid grid-cols-2 gap-4">
                        {dynamicSections.map((s) => {
                          const key = s.code;
                          const jp = s.japaneseName || s.name;
                          const label = s.englishName || s.name;
                          const secTotal = computedCategoryTotals[key] || 60;
                          const secPassVal = editCategoryPassMarks[key];
                          const isInvalid = secPassVal > secTotal;
                          return (
                            <div key={key} className="space-y-1.5">
                              <Label className="text-xs font-bold text-foreground">
                                {jp} <span className="font-normal text-muted-foreground">({label})</span>
                              </Label>
                              <div className="relative">
                                <Input
                                  type="number"
                                  min={0}
                                  max={secTotal}
                                  className={cn(
                                    "h-9 text-sm pr-12 rounded-xl focus:ring-primary",
                                    isInvalid && "border-destructive focus:ring-destructive text-destructive"
                                  )}
                                  value={secPassVal}
                                  onChange={e => setEditCategoryPassMarks(prev => ({ ...prev, [key]: Math.max(0, Number(e.target.value) || 0) }))}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground font-medium pointer-events-none">/ {secTotal}</span>
                              </div>
                              {isInvalid && (
                                <p className="text-[10px] text-destructive font-medium">Exceeds section total!</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </SettingRow>
                  )}

                  {isAdmin && (
                    <>
                      <div className="pt-4 pb-2">
                        <h3 className="font-semibold text-lg">Pricing & Access</h3>
                      </div>

                      <SettingRow title="Price (BDT)" description="0 means this test is free for all students.">
                        <Input
                          type="number"
                          min={0}
                          step={10}
                          className="h-9 text-sm rounded-xl max-w-[150px]"
                          value={editPrice}
                          onChange={e => setEditPrice(Math.max(0, Number(e.target.value) || 0))}
                        />
                      </SettingRow>

                      <SettingRow title="Attempt Limit" description="Maximum number of times a student can take this test per purchase.">
                        <Input
                          type="number"
                          min={1}
                          max={10}
                          className="h-9 text-sm rounded-xl max-w-[150px]"
                          value={editAttemptLimit}
                          onChange={e => setEditAttemptLimit(Math.max(1, Number(e.target.value) || 1))}
                        />
                      </SettingRow>

                      <SettingRow title="Access Duration (Days)" description="Leave blank for lifetime access.">
                        <Input
                          type="number"
                          min={1}
                          className="h-9 text-sm rounded-xl max-w-[150px]"
                          placeholder="Lifetime"
                          value={editAccessDays}
                          onChange={e => setEditAccessDays(e.target.value)}
                        />
                      </SettingRow>

                      <SettingRow title="Bundle Eligibility" description="Allow grouping in discount deals.">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editBundleEligible}
                            onChange={e => setEditBundleEligible(e.target.checked)}
                            className="rounded border-border text-primary focus:ring-primary size-5"
                          />
                          <span className="text-sm font-medium">Include in bundles</span>
                        </label>
                      </SettingRow>

                      <div className="pt-4 pb-2">
                        <h3 className="font-semibold text-lg text-amber-600 dark:text-amber-500">Advanced</h3>
                      </div>

                      <SettingRow title="Assign Teacher" description="Give a teacher permission to edit and review this mock test.">
                        <div className="flex gap-2">
                          <select
                            className="flex-1 max-w-sm h-9 px-3 rounded-xl border border-border bg-background text-sm outline-none focus:ring-2 focus:ring-primary"
                            value={teacherIdToAssign}
                            onChange={e => setTeacherIdToAssign(e.target.value ? Number(e.target.value) : "")}
                          >
                            <option value="">Select teacher…</option>
                            {teacherOptions.map(t => (
                              <option key={t.id} value={t.id}>{t.label}</option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-9 px-4 rounded-xl"
                            disabled={!teacherIdToAssign || assignTeacherMutation.isPending}
                            onClick={() => assignTeacherMutation.mutate(Number(teacherIdToAssign))}
                          >
                            <Users size={14} className="mr-2" />
                            Assign
                          </Button>
                        </div>
                      </SettingRow>

                      <div className="p-5 flex items-center justify-between gap-4 border border-amber-200/60 dark:border-amber-900/40 bg-amber-50/30 dark:bg-amber-950/20 rounded-xl mb-4 mt-2">
                        <div className="space-y-1">
                          <h3 className="font-medium text-sm text-foreground flex items-center gap-1.5">
                            <AlertCircle className="size-4 text-amber-600 dark:text-amber-500" />
                            Re-grade Past Attempts
                          </h3>
                          <p className="text-[13px] text-muted-foreground leading-relaxed">
                            After fixing a wrong answer, re-score all students' submitted results using the current correct answers.
                          </p>
                        </div>
                        <div className="shrink-0">
                          <Button
                          variant="outline"
                          className="h-9 rounded-xl border-amber-400 text-amber-700 hover:bg-amber-50 dark:border-amber-600 dark:text-amber-400 dark:hover:bg-amber-950"
                          onClick={async () => {
                            setRegradePreview(null);
                            setShowRegradeDialog(true);
                            setRegradeLoading(true);
                            try {
                              const preview = await regradeAttempts(mockTestId, true);
                              setRegradePreview(preview);
                            } catch (e: any) {
                              toast.error(e?.message || "Failed to load re-grade preview");
                              setShowRegradeDialog(false);
                            } finally {
                              setRegradeLoading(false);
                            }
                          }}
                        >
                          <AlertCircle size={14} className="mr-2" />
                          Re-grade Attempts
                        </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-border flex justify-end sticky bottom-0 bg-card z-10 pb-6 -mx-6 px-6 pt-4">
                  <Button
                    className="h-10 px-8 rounded-xl"
                    disabled={updateMetaMutation.isPending}
                    onClick={() => {
                      const isSectional = activeConfig?.passCriteria === "SECTIONAL_REQUIRED";
                      const totalDuration = !isSectional ? editDuration : (
                        Object.values(sectionTimings).reduce((a, b) => a + b, 0)
                      );

                      if (editPassingMarks > targetTotalMarks) {
                        toast.error(`Overall pass mark (${editPassingMarks}) cannot exceed target total marks (${targetTotalMarks}).`);
                        return;
                      }

                      if (isSectional) {
                        for (const sec of dynamicSections) {
                          const val = editCategoryPassMarks[sec.code] || 0;
                          if (val > targetTotalMarks) {
                            toast.error(`${sec.name} pass mark (${val}) cannot exceed target total marks (${targetTotalMarks}).`);
                            return;
                          }
                        }
                        localStorage.setItem(
                          SECTION_TIMING_STORAGE_KEY(mockTestId),
                          JSON.stringify(sectionTimings)
                        );
                      }

                      updateMetaMutation.mutate({
                        title: editTitle.trim(),
                        position: editPosition,
                        duration: totalDuration,
                        level: activeConfig?.levels?.length === 1 ? activeConfig.levels[0].code : joinMockTestLevels(editLevels),
                        sectionDurations: !isSectional ? null : JSON.stringify(sectionTimings),
                        passingMarks: editPassingMarks,
                        categoryPassMarks: !isSectional ? null : editCategoryPassMarks,
                        price: editPrice,
                        attemptLimit: Math.max(1, editAttemptLimit),
                        bundleEligible: editBundleEligible,
                        accessDays: editAccessDays === "" ? null : Number(editAccessDays),
                      });
                    }}
                  >
                    {updateMetaMutation.isPending ? <Loader2 className="animate-spin size-4 mr-2" /> : <Save size={16} className="mr-2" />}
                    Save All Settings
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            {/* Add Question dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="h-8 text-xs gap-1.5" disabled={isGlobalSaving}>
                  <Plus size={13} />
                  Add Question
                  <ChevronDown size={12} className="opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => {
                  const d = emptyUiQuestion(activeSection === "ALL" ? "" : activeSection);
                  d.sortOrder = unifiedFlow.length > 0
                    ? Math.max(...unifiedFlow.map(f => f.sortOrder)) + 1
                    : 1;
                  setDraftQuestions(prev => [...prev, d]);
                  setActiveItemId(d.id);
                  toast.success("Draft standalone question added");
                }}>
                  <FileText className="size-4 mr-2 text-muted-foreground" />
                  Add Standalone Question
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => addScenarioMutation.mutate()}>
                  <Layers className="size-4 mr-2 text-muted-foreground" />
                  Add Scenario Question Block
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Commit Questions — saves draft questions to database */}
            {draftQuestions.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1.5 border-amber-400 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-950/30"
                onClick={() => publishDraftsMutation.mutate()}
                disabled={publishDraftsMutation.isPending}
              >
                {publishDraftsMutation.isPending
                  ? <Loader2 className="animate-spin size-3.5" />
                  : <Save size={13} />
                }
                Commit {draftQuestions.length > 0 && `(${draftQuestions.length})`}
              </Button>
            )}

            {/* Publish / Unpublish — controls student visibility */}
            {mockTest?.isPublished ? (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1.5 border-emerald-300 text-emerald-700 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 dark:border-emerald-700 dark:text-emerald-400 dark:hover:bg-rose-950/20 dark:hover:text-rose-400 transition-colors"
                disabled={setPublishedMutation.isPending}
                onClick={() => {
                  if (window.confirm("Unpublish this test? Students will no longer see it.")) {
                    setPublishedMutation.mutate(false);
                  }
                }}
              >
                {setPublishedMutation.isPending
                  ? <Loader2 className="animate-spin size-3.5" />
                  : <Eye size={13} />
                }
                Published ✓
              </Button>
            ) : (
              <Button
                size="sm"
                className="h-8 text-xs gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground"
                disabled={setPublishedMutation.isPending}
                onClick={() => setPublishedMutation.mutate(true)}
              >
                {setPublishedMutation.isPending
                  ? <Loader2 className="animate-spin size-3.5" />
                  : <Send size={13} />
                }
                Publish
              </Button>
            )}
          </div>
        </div>

        {/* ── Section Filter Tabs ───────────────────────────────────────── */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {["ALL", ...dynamicSections.map(s => s.code)].map(tab => {
            const isActive = activeSection === tab;
            if (tab === "ALL") {
              return (
                <button
                  key="ALL"
                  type="button"
                  onClick={() => { setActiveSection("ALL"); setActiveItemId(null); }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${isActive
                    ? "bg-foreground text-background border-foreground"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                    }`}
                >
                  All Sections
                </button>
              );
            }
            const meta = getSectionMeta(tab);
            return (
              <button
                key={tab}
                type="button"
                onClick={() => { setActiveSection(tab); setActiveItemId(null); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${isActive
                  ? `${meta.bg} ${meta.color} border-current`
                  : "border-border text-muted-foreground hover:text-foreground"
                  }`}
              >
                {meta.jp} <span className="opacity-60 font-normal">({meta.label})</span>
              </button>
            );
          })}
          <div className="ml-auto text-[11px] text-muted-foreground flex items-center gap-3 shrink-0">
            <span>{totalQuestions} Q</span>
            <span className="font-semibold text-foreground">{totalMarks} 点</span>
          </div>
        </div>

        {/* ── Main 3-Panel Workspace ────────────────────────────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 flex-1 min-h-0 items-stretch">

          {/* ── LEFT: Blueprint Flow ─────────────────────────────────────── */}
          <div className="xl:col-span-3 flex flex-col bg-card border border-border/70 rounded-2xl overflow-hidden shadow-sm min-h-[500px] xl:min-h-0 xl:h-full">
            {/* Panel Header */}
            <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between bg-card">
              <div>
                <h3 className="text-xs font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <BookOpen className="size-3.5 text-primary" />
                  Exam Blueprint
                </h3>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {unifiedFlow.length} item{unifiedFlow.length !== 1 ? "s" : ""}
                  {activeSection !== "ALL" && ` · ${activeSection}`}
                </p>
              </div>
              <span className="text-[10px] font-mono text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded border border-border/40">
                {unifiedFlow.length}
              </span>
            </div>

            {/* Flow List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2 scrollbar-thin">
              {unifiedFlow.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground gap-2.5">
                  <div className="w-12 h-12 rounded-full bg-primary/5 border border-primary/10 flex items-center justify-center text-primary">
                    <Layers className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground">No items yet</p>
                    <p className="text-[10px] mt-0.5 max-w-[160px] mx-auto text-muted-foreground">
                      Use "Add Question" above to begin authoring
                    </p>
                  </div>
                </div>
              ) : (
                <AnimatePresence initial={false}>
                  {unifiedFlow.map((item, index) => {
                    const isActive = activeItemId === item.id;
                    const itemLabel = `${index + 1}`;

                    // ── Scenario Node ──────────────────────────────────
                    if (item.type === "scenario") {
                      const s = item.scenario;
                      const subQuestions = [
                        ...existingEditUi.filter(q => q.scenarioId === s.id),
                        ...draftQuestions.filter(q => q.scenarioId === s.id),
                      ].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                      const isCollapsed = collapsedScenarios[s.id] || false;

                      return (
                        <motion.div
                          key={s.id}
                          layout
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.18 }}
                          className={`rounded-xl border transition-all ${isActive
                            ? "border-primary/50 bg-primary/[0.03] ring-1 ring-primary/20"
                            : "border-border/50 hover:border-border bg-secondary/[0.03]"
                            }`}
                        >
                          {/* Scenario Header */}
                          <div
                            className="px-3 py-2.5 flex items-center gap-2 cursor-pointer"
                            onClick={() => setActiveItemId(s.id)}
                          >
                            <button
                              type="button"
                              className="p-0.5 rounded text-muted-foreground hover:text-foreground shrink-0 transition-colors"
                              onClick={e => {
                                e.stopPropagation();
                                setCollapsedScenarios(prev => ({ ...prev, [s.id]: !isCollapsed }));
                              }}
                            >
                              <ChevronRight
                                size={13}
                                className={`transition-transform duration-200 ${!isCollapsed ? "rotate-90" : ""}`}
                              />
                            </button>
                            <span className="text-[9px] font-bold text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded font-mono shrink-0">
                              BLOCK {itemLabel}
                            </span>
                            <span className="text-xs font-semibold text-foreground truncate flex-1">
                              {s.audioUrl ? "🎧" : s.imageUrl ? "🖼️" : "📝"}{" "}
                              {s.instructionsText
                                ? s.instructionsText.slice(0, 22) + (s.instructionsText.length > 22 ? "…" : "")
                                : "Scenario Passage"
                              }
                            </span>
                            <div className="flex items-center gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
                              <button
                                className="size-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                                disabled={index === 0}
                                onClick={() => handleReorder(index, "up")}
                              >
                                <ArrowUp size={10} />
                              </button>
                              <button
                                className="size-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                                disabled={index === unifiedFlow.length - 1}
                                onClick={() => handleReorder(index, "down")}
                              >
                                <ArrowDown size={10} />
                              </button>
                              <button
                                className="size-5 flex items-center justify-center rounded text-destructive hover:bg-destructive/10 transition-colors"
                                onClick={() => setDeleteConfirmId(s.id)}
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          </div>

                          {/* Nested Questions */}
                          {!isCollapsed && (
                            <div className="px-3 pb-3 border-t border-border/30">
                              <div className="pl-3 border-l-2 border-primary/15 pt-2 space-y-1.5">
                                {subQuestions.map((q, qIdx) => {
                                  const isSubActive = activeItemId === q.id;
                                  const isDraft = draftQuestions.some(d => d.id === q.id);
                                  const sm = getSectionMeta(q.examCategory);
                                  return (
                                    <div
                                      key={q.id}
                                      className={`px-2 py-1.5 rounded-xl border text-xs flex items-center justify-between gap-1.5 cursor-pointer transition-all ${isSubActive
                                        ? "border-primary/40 bg-primary/[0.08] text-primary"
                                        : "border-border/30 hover:border-border/70 text-muted-foreground hover:text-foreground bg-background/40"
                                        }`}
                                      onClick={() => setActiveItemId(q.id)}
                                    >
                                      <span className="truncate flex items-center gap-1.5 min-w-0">
                                        {isDraft && (
                                          <span className="text-[8px] font-bold text-amber-500 bg-amber-500/10 px-1 py-0.5 rounded border border-amber-500/20 shrink-0">
                                            DRAFT
                                          </span>
                                        )}
                                        <span className={`text-[8px] font-bold px-1 py-0.5 rounded border shrink-0 ${sm.bg} ${sm.color}`}>
                                          {q.examCategory.slice(0, 3)}
                                        </span>
                                        <span className="font-mono text-[9px] opacity-60 shrink-0">
                                          Q{itemLabel}.{qIdx + 1}
                                        </span>
                                        <span className="truncate font-japanese">
                                          {q.questionText || "(empty)"}
                                        </span>
                                      </span>
                                      <div className="flex items-center gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
                                        <button
                                          className="size-4 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                                          disabled={qIdx === 0}
                                          onClick={() => handleMoveSubQuestion(q.id, "up", s.id)}
                                        >
                                          <ArrowUp size={8} />
                                        </button>
                                        <button
                                          className="size-4 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                                          disabled={qIdx === subQuestions.length - 1}
                                          onClick={() => handleMoveSubQuestion(q.id, "down", s.id)}
                                        >
                                          <ArrowDown size={8} />
                                        </button>
                                        <button
                                          className="size-4 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                                          onClick={() => handleDuplicateQuestion(q)}
                                        >
                                          <Copy size={8} />
                                        </button>
                                        <button
                                          className="size-4 flex items-center justify-center text-destructive transition-colors"
                                          onClick={() => handleDeleteQuestion(q.id, isDraft)}
                                        >
                                          <Trash2 size={8} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}

                                {/* Add nested question */}
                                <button
                                  type="button"
                                  className="w-full text-left text-[10px] text-primary font-medium px-2 py-1.5 rounded-md border border-dashed border-primary/25 hover:bg-primary/5 transition-colors flex items-center gap-1"
                                  onClick={() => {
                                    const d = emptyUiQuestion(activeSection === "ALL" ? "" : activeSection);
                                    d.scenarioId = s.id;
                                    const maxSort = Math.max(
                                      ...draftQuestions.filter(q => q.scenarioId === s.id).map(q => q.sortOrder),
                                      ...existingEditUi.filter(q => q.scenarioId === s.id).map(q => q.sortOrder),
                                      -1
                                    );
                                    d.sortOrder = maxSort + 1;
                                    setDraftQuestions(prev => [...prev, d]);
                                    setActiveItemId(d.id);
                                  }}
                                >
                                  <Plus size={9} />
                                  Add nested question
                                </button>
                              </div>
                            </div>
                          )}
                        </motion.div>
                      );
                    }

                    // ── Standalone Question Node ───────────────────────
                    const q = item.question;
                    const isDraft = item.type === "draft-standalone";
                    const sm = getSectionMeta(q.examCategory);

                    return (
                      <motion.div
                        key={q.id}
                        layout
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.18 }}
                        className={`px-3 py-2.5 rounded-xl border flex items-center gap-2 cursor-pointer transition-all ${isActive
                          ? "border-primary/50 bg-primary/[0.03] ring-1 ring-primary/20"
                          : "border-border/50 hover:border-border bg-card hover:bg-secondary/10"
                          }`}
                        onClick={() => setActiveItemId(q.id)}
                      >
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border shrink-0 ${sm.bg} ${sm.color}`}>
                          {q.examCategory.slice(0, 3)}
                        </span>
                        <span className="text-[10px] font-mono text-muted-foreground/60 shrink-0">Q{itemLabel}</span>
                        <span className="text-xs font-medium truncate flex-1 flex items-center gap-1.5">
                          {isDraft && (
                            <span className="text-[8px] font-bold text-amber-500 bg-amber-500/10 px-1 py-0.5 rounded border border-amber-500/20 shrink-0">
                              DRAFT
                            </span>
                          )}
                          <span className="truncate font-japanese">
                            {q.questionText || "Standalone Question"}
                          </span>
                        </span>
                        <div className="flex items-center gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
                          <button
                            className="size-5 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                            disabled={index === 0}
                            onClick={() => handleReorder(index, "up")}
                          >
                            <ArrowUp size={10} />
                          </button>
                          <button
                            className="size-5 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                            disabled={index === unifiedFlow.length - 1}
                            onClick={() => handleReorder(index, "down")}
                          >
                            <ArrowDown size={10} />
                          </button>
                          <button
                            className="size-5 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                            onClick={() => handleDuplicateQuestion(q)}
                          >
                            <Copy size={10} />
                          </button>
                          <button
                            className="size-5 flex items-center justify-center text-destructive transition-colors"
                            onClick={() => handleDeleteQuestion(q.id, isDraft)}
                          >
                            <Trash2 size={10} />
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
            </div>
          </div>

          {/* ── CENTER: Question Content Editor ──────────────────────────── */}
          <div className="xl:col-span-5 flex flex-col bg-card border border-border/70 rounded-2xl overflow-hidden shadow-sm min-h-[600px] xl:min-h-0 xl:h-full">

            {activeQuestion ? (
              /* ── Standalone / Nested Question Editor ─────────────────── */
              <div className="flex flex-col h-full">
                {/* Editor Header */}
                <div className="px-5 py-3.5 border-b border-border/50 flex items-center justify-between bg-card shrink-0">
                  <h3 className="text-xs font-bold text-foreground flex items-center gap-1.5">
                    <Edit3 className="size-3.5 text-primary" />
                    Question Editor
                  </h3>
                  <Button
                    size="sm"
                    className="h-7 text-xs gap-1.5 rounded-xl"
                    disabled={savingQuestionId === activeQuestion.id || isGlobalSaving}
                    onClick={() => {
                      if (existingEditUi.some(q => q.id === activeQuestion.id)) {
                        saveQuestionMutation.mutate(activeQuestion);
                      } else {
                        toast.success("Draft updated. Click Publish above to commit.");
                      }
                    }}
                  >
                    {savingQuestionId === activeQuestion.id
                      ? <Loader2 className="animate-spin size-3" />
                      : <Save size={12} />
                    }
                    Save Change
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-5 scrollbar-thin">
                  {/* Section + Marks row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold flex items-center gap-1">
                        Exam Section
                        {!activeQuestion.examCategory && (
                          <span className="text-destructive text-[10px] font-bold ml-1">⚠ Required</span>
                        )}
                      </Label>
                      <select
                        className={`w-full h-9 px-2.5 rounded-xl border bg-background text-xs font-medium focus:ring-1 focus:ring-primary outline-none ${!activeQuestion.examCategory ? "border-destructive ring-1 ring-destructive" : "border-border"}`}
                        value={activeQuestion.examCategory}
                        onChange={e => updateActiveQuestion(q => ({ ...q, examCategory: e.target.value }))}
                      >
                        <option value="">Select Section</option>
                        {dynamicSections.map(s => (
                          <option key={s.code} value={s.code}>{s.japaneseName ? `${s.japaneseName} (${s.englishName || s.name})` : s.englishName || s.name}</option>
                        ))}
                      </select>
                      {!activeQuestion.examCategory && (
                        <p className="text-[10px] text-destructive">Section is required — question will not appear in the correct section for students without it.</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold">Marks (点)</Label>
                      <Input
                        type="number"
                        min={0}
                        className="h-9 text-sm rounded-xl"
                        value={activeQuestion.marks ?? 1}
                        onChange={e => {
                          const v = Number(e.target.value);
                          updateActiveQuestion(q => ({ ...q, marks: Number.isFinite(v) ? v : 1 }));
                        }}
                      />
                    </div>
                  </div>


                  {/* Question Stem */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold">Question Stem</Label>
                      <TextFormattingToolbar
                        textareaRef={questionStemRef}
                        value={activeQuestion.questionText}
                        onChange={val => updateActiveQuestion(q => ({ ...q, questionText: val }))}
                      />
                    </div>
                    <textarea
                      ref={questionStemRef}
                      className="w-full min-h-[90px] p-3 rounded-xl border border-border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none font-japanese leading-relaxed"
                      value={activeQuestion.questionText}
                      onChange={e => updateActiveQuestion(q => ({ ...q, questionText: e.target.value }))}
                      placeholder="e.g. 下の文の（　）に入る最も良いものを選びなさい。"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Tip: Use <code className="bg-muted px-1 rounded">Kanji{"{Furigana}"}</code> pattern for inline ruby text.
                    </p>
                  </div>

                  {/* Media Attachments */}
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">Attach Media</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center justify-center gap-1.5 h-9 rounded-xl border border-primary/25 bg-primary/[0.02] hover:bg-primary/5 text-primary text-xs font-semibold cursor-pointer transition-all">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const r = await uploadToS3(file, "mock-test-question");
                              updateActiveQuestion(q => ({ ...q, imageUrl: r.url }));
                              toast.success("Image attached");
                            } catch { toast.error("Upload failed"); }
                          }}
                        />
                        <ImageIcon size={13} />
                        Upload Image
                      </label>
                      <label className="flex items-center justify-center gap-1.5 h-9 rounded-xl border border-primary/25 bg-primary/[0.02] hover:bg-primary/5 text-primary text-xs font-semibold cursor-pointer transition-all">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          onChange={async e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const r = await uploadToS3(file, "mock-test-question");
                              updateActiveQuestion(q => ({ ...q, audioUrl: r.url }));
                              toast.success("Audio attached");
                            } catch { toast.error("Upload failed"); }
                          }}
                        />
                        <Volume2 size={13} />
                        Upload Audio
                      </label>
                    </div>
                    {(activeQuestion.imageUrl || activeQuestion.audioUrl) && (
                      <div className="rounded-xl border border-border/70 p-3 bg-secondary/10 space-y-2">
                        {activeQuestion.imageUrl && (
                          <div className="relative inline-block">
                            <img src={activeQuestion.imageUrl} alt="" className="max-h-28 rounded-lg border border-border object-contain" />
                            <button
                              className="absolute -top-1.5 -right-1.5 size-5 bg-destructive text-white rounded-full text-xs font-bold flex items-center justify-center hover:bg-destructive/80 transition-colors"
                              onClick={() => updateActiveQuestion(q => ({ ...q, imageUrl: "" }))}
                            >×</button>
                          </div>
                        )}
                        {activeQuestion.audioUrl && (
                          <div className="flex items-center gap-2 bg-background p-2 rounded-lg border border-border">
                            <audio controls className="flex-1 h-8" src={activeQuestion.audioUrl} />
                            <button
                              className="text-destructive hover:text-destructive/80 transition-colors"
                              onClick={() => updateActiveQuestion(q => ({ ...q, audioUrl: "" }))}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Listening CBT Controls — shown when question has audio */}
                    {activeQuestion.audioUrl && (
                      <div className="p-3 bg-purple-500/5 rounded-xl border border-purple-500/20 text-xs space-y-3">
                        <span className="font-bold text-foreground flex items-center gap-1.5">
                          <AudioLines size={13} className="text-purple-500" />
                          Listening CBT Controls
                        </span>
                        <div className="space-y-1">
                          <Label className="text-[10px] text-muted-foreground">Play Limit</Label>
                          <select
                            className="w-full h-8 rounded-xl border border-border bg-background px-2 text-[11px] outline-none"
                            value={getPlayLimitFromUrl(activeQuestion.audioUrl) || ""}
                            onChange={(e) => {
                              const newUrl = setPlayLimitInUrl(activeQuestion.audioUrl, e.target.value);
                              updateActiveQuestion((prev) => ({ ...prev, audioUrl: newUrl || "" }));
                            }}
                          >
                            <option value="">Default (Exam Rules)</option>
                            <option value="1">1× (Official JLPT)</option>
                            <option value="2">2× (Official JFT)</option>
                            <option value="unlimited">Unlimited</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* MCQ Options + Explanation (grouped) */}
                  <div className="space-y-4">
                    {/* MCQ Options */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs font-semibold">Option Layout / Type</Label>
                        <div className="flex rounded-lg border border-border p-0.5 bg-secondary/20 gap-0.5 mt-1 select-none">
                          {(["TEXT", "IMAGE", "MIXED"] as const).map((t) => (
                            <button
                              key={t}
                              type="button"
                              onClick={() => {
                                updateActiveQuestion(q => ({
                                  ...q,
                                  optionType: t,
                                }));
                              }}
                              className={cn(
                                "flex-1 py-1 text-[11px] font-semibold rounded-md transition-all",
                                (activeQuestion.optionType || "TEXT") === t
                                  ? "bg-background text-foreground shadow-xs"
                                  : "text-muted-foreground hover:text-foreground"
                              )}
                            >
                              {t === "TEXT" ? "Text Only" : t === "IMAGE" ? "Image Only" : "Text + Image"}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2.5">
                        <Label className="text-xs font-semibold">Answer Options (MCQ)</Label>
                        <div className="space-y-2.5">
                          {activeQuestion.options.map((opt, oIdx) => {
                            const isCorrect = activeQuestion.correctId === opt.id;
                            const currentLayout = activeQuestion.optionType || "TEXT";
                            return (
                              <div key={opt.id} className={`p-3 rounded-xl border space-y-2.5 transition-all ${isCorrect ? "border-emerald-500/40 bg-emerald-500/[0.03]" : "border-border/60 bg-secondary/[0.02]"}`}>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => updateActiveQuestion(q => ({ ...q, correctId: opt.id }))}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 border text-xs font-bold transition-all ${isCorrect
                                      ? "bg-emerald-500/10 border-emerald-500 text-emerald-600 dark:text-emerald-400"
                                      : "border-border hover:border-emerald-500/50 text-muted-foreground bg-background"
                                      }`}
                                  >
                                    {mcqDisplayBadge(oIdx)}
                                  </button>

                                  {currentLayout !== "IMAGE" ? (
                                    <div className="relative flex items-center flex-1 min-w-0">
                                      <Input
                                        id={`opt-input-${activeQuestion.id}-${opt.id}`}
                                        className="h-8 text-sm font-japanese flex-1 rounded-xl bg-background pr-8"
                                        value={opt.text}
                                        onChange={e => {
                                          const val = e.target.value;
                                          updateActiveQuestion(q => ({
                                            ...q,
                                            options: q.options.map(o => o.id === opt.id ? { ...o, text: val } : o),
                                          }));
                                        }}
                                        placeholder={`Option ${mcqDisplayBadge(oIdx)} text`}
                                      />
                                      <button
                                        type="button"
                                        className="absolute right-2 text-primary hover:text-primary/80 transition-all p-1"
                                        title="Insert Kanji{Furigana}"
                                        onClick={() => {
                                          const el = document.getElementById(`opt-input-${activeQuestion.id}-${opt.id}`) as HTMLInputElement;
                                          if (el) {
                                            insertFormatting(el, "furigana", opt.text, (newVal) => {
                                              updateActiveQuestion(q => ({
                                                ...q,
                                                options: q.options.map(o => o.id === opt.id ? { ...o, text: newVal } : o),
                                              }));
                                            });
                                          }
                                        }}
                                      >
                                        <Languages size={13} />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex-1 text-xs text-muted-foreground italic pl-2 py-1 bg-secondary/5 rounded border border-border/30">
                                      Option image will represent Choice {mcqDisplayBadge(oIdx)}
                                    </div>
                                  )}

                                  <div className="flex items-center gap-0.5 shrink-0">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="size-8 text-muted-foreground hover:text-foreground"
                                      disabled={oIdx === 0}
                                      onClick={() => moveOption(oIdx, "up")}
                                      title="Move Up"
                                    >
                                      <ArrowUp size={13} />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="size-8 text-muted-foreground hover:text-foreground"
                                      disabled={oIdx === activeQuestion.options.length - 1}
                                      onClick={() => moveOption(oIdx, "down")}
                                      title="Move Down"
                                    >
                                      <ArrowDown size={13} />
                                    </Button>
                                    <button
                                      type="button"
                                      className="size-8 flex items-center justify-center text-destructive hover:bg-destructive/10 rounded-lg transition-colors disabled:opacity-30"
                                      disabled={activeQuestion.options.length <= 2}
                                      onClick={() => updateActiveQuestion(q => {
                                        const next = q.options.filter(o => o.id !== opt.id);
                                        const correctId = next.some(o => o.id === q.correctId) ? q.correctId : (next[0]?.id ?? "");
                                        return { ...q, options: next, correctId };
                                      })}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  </div>
                                </div>

                                {/* Option image attach/preview */}
                                {currentLayout !== "TEXT" && (
                                  <div className="flex items-center gap-3 pl-10">
                                    {opt.imageUrl ? (
                                      <div className="flex items-center gap-2 bg-secondary/20 border border-border p-1.5 rounded-lg">
                                        <img src={opt.imageUrl} alt="" className="size-10 object-contain bg-white rounded border border-border" />
                                        <span className="text-[10px] text-muted-foreground font-sans truncate max-w-[120px]">Image attached</span>
                                        <button
                                          type="button"
                                          onClick={() => updateActiveQuestion(q => ({
                                            ...q,
                                            options: q.options.map(o => o.id === opt.id ? { ...o, imageUrl: "" } : o),
                                          }))}
                                          className="text-destructive font-bold text-xs hover:text-destructive/80 px-1"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    ) : (
                                      <label className="cursor-pointer hover:text-primary transition-colors flex items-center gap-1.5 border border-dashed border-border/80 px-2.5 py-1 rounded-lg text-[10px] bg-background hover:bg-secondary/5 select-none">
                                        <input
                                          type="file"
                                          accept="image/*"
                                          className="hidden"
                                          onChange={async e => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;
                                            try {
                                              const r = await uploadToS3(file, "exam-option");
                                              updateActiveQuestion(q => ({
                                                ...q,
                                                options: q.options.map(o => o.id === opt.id ? { ...o, imageUrl: r.url } : o),
                                              }));
                                              toast.success("Option image attached");
                                            } catch { toast.error("Upload failed"); }
                                          }}
                                        />
                                        <ImageIcon size={10} className="text-muted-foreground" />
                                        <span>Upload Choice Image</span>
                                      </label>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="w-full h-9 rounded-xl border border-dashed border-border/70 hover:border-primary/40 text-xs font-medium text-muted-foreground hover:text-primary transition-all flex items-center justify-center gap-1.5 bg-background"
                        onClick={() => updateActiveQuestion(q => ({
                          ...q,
                          options: [...q.options, { id: newMcqOptionId(), text: "", imageUrl: "", audioUrl: "" }],
                        }))}
                      >
                        <Plus size={13} />
                        Add Answer Option
                      </button>
                    </div>{/* CLOSE MCQ Options wrapper */}

                    {/* ── Answer Explanation ───────────────────────── */}
                    <div className="space-y-1.5 pt-1">
                      <Label className="text-xs font-semibold flex items-center gap-1.5">
                        <span>解説 / Answer Explanation</span>
                        <span className="text-[10px] font-normal text-muted-foreground">(Optional — shown to students in review mode)</span>
                      </Label>
                      <textarea
                        className="w-full min-h-[72px] p-3 rounded-xl border border-border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none font-japanese leading-relaxed"
                        value={activeQuestion.answerExplanation ?? ""}
                        onChange={e => updateActiveQuestion(q => ({ ...q, answerExplanation: e.target.value }))}
                        placeholder="e.g. この答えが正しい理由は… / Explain why this is the correct answer..."
                      />
                    </div>

                  </div>
                </div>
              </div>

            ) : activeScenario ? (
              /* ── Scenario Passage Editor ─────────────────────────────── */
              <div className="flex flex-col h-full">
                {/* Scenario Editor Header */}
                <div className="px-5 py-3.5 border-b border-border/50 flex items-center justify-between bg-card shrink-0">
                  <h3 className="text-xs font-bold text-foreground flex items-center gap-1.5">
                    <Layers className="size-3.5 text-primary" />
                    Scenario Block Editor
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="h-7 text-xs gap-1.5 rounded-xl"
                      disabled={scenarioMediaSavingId === activeScenario.id || isGlobalSaving}
                      onClick={() => updateScenarioMediaMutation.mutate({
                        scenarioId: activeScenario.id,
                        data: { instructionsText: editingScenarioText.trim() || null },
                      })}
                    >
                      {scenarioMediaSavingId === activeScenario.id
                        ? <Loader2 className="animate-spin size-3" />
                        : <Save size={12} />
                      }
                      Save Change
                    </Button>
                    <button
                      className="size-7 flex items-center justify-center text-destructive hover:bg-destructive/10 rounded-xl transition-colors"
                      onClick={() => setDeleteConfirmId(activeScenario.id)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-5 scrollbar-thin">
                  {/* Passage Text */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold">Passage / Instructions</Label>
                      <TextFormattingToolbar
                        textareaRef={scenarioPassageRef}
                        value={editingScenarioId === activeScenario.id ? editingScenarioText : (activeScenario.instructionsText ?? "")}
                        onChange={val => {
                          setEditingScenarioId(activeScenario.id);
                          setEditingScenarioText(val);
                        }}
                      />
                    </div>
                    <textarea
                      ref={scenarioPassageRef}
                      className="w-full min-h-[140px] p-3 rounded-xl border border-border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none font-japanese leading-relaxed"
                      value={editingScenarioId === activeScenario.id ? editingScenarioText : (activeScenario.instructionsText ?? "")}
                      onChange={e => {
                        setEditingScenarioId(activeScenario.id);
                        setEditingScenarioText(e.target.value);
                      }}
                      onBlur={() => {
                        if (editingScenarioId === activeScenario.id) {
                          updateScenarioMediaMutation.mutate({
                            scenarioId: activeScenario.id,
                            data: { instructionsText: editingScenarioText.trim() || null },
                          });
                        }
                      }}
                      placeholder="Type the reading passage or listening instructions here..."
                    />
                  </div>

                  {/* Passage Media */}
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">Passage Media</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center justify-center gap-1.5 h-9 rounded-xl border border-border/60 hover:border-primary/30 text-muted-foreground hover:text-primary text-xs font-medium cursor-pointer transition-all">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const r = await uploadToS3(file, "mock-test-scenario");
                              updateScenarioMediaMutation.mutate({ scenarioId: activeScenario.id, data: { imageUrl: r.url } });
                            } catch { toast.error("Upload failed"); }
                          }}
                        />
                        <ImageIcon size={13} />
                        Attach Image
                      </label>
                      <label className="flex items-center justify-center gap-1.5 h-9 rounded-xl border border-border/60 hover:border-purple-500/30 text-muted-foreground hover:text-purple-500 text-xs font-medium cursor-pointer transition-all">
                        <input
                          type="file"
                          accept="audio/*"
                          className="hidden"
                          onChange={async e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const r = await uploadToS3(file, "mock-test-scenario");
                              updateScenarioMediaMutation.mutate({ scenarioId: activeScenario.id, data: { audioUrl: r.url } });
                            } catch { toast.error("Upload failed"); }
                          }}
                        />
                        <AudioLines size={13} />
                        Attach Audio
                      </label>
                    </div>

                    {(activeScenario.imageUrl || activeScenario.audioUrl) && (
                      <div className="rounded-xl border border-border/70 p-3 bg-secondary/10 space-y-2">
                        {activeScenario.imageUrl && (
                          <div className="relative inline-block">
                            <img src={activeScenario.imageUrl} alt="" className="max-h-28 rounded-lg border border-border object-contain" />
                            <button
                              className="absolute -top-1.5 -right-1.5 size-5 bg-destructive text-white rounded-full text-xs flex items-center justify-center hover:bg-destructive/80"
                              onClick={() => updateScenarioMediaMutation.mutate({ scenarioId: activeScenario.id, data: { imageUrl: null } })}
                            >×</button>
                          </div>
                        )}
                        {activeScenario.audioUrl && (
                          <div className="flex items-center gap-2 bg-background p-2 rounded-lg border border-border">
                            <audio controls className="flex-1 h-8" src={activeScenario.audioUrl} />
                            <button
                              className="text-destructive hover:text-destructive/80 transition-colors"
                              onClick={() => updateScenarioMediaMutation.mutate({ scenarioId: activeScenario.id, data: { audioUrl: null } })}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Listening CBT Controls */}
                  {activeScenario.audioUrl && (
                    <div className="p-3 bg-purple-500/5 rounded-xl border border-purple-500/20 text-xs space-y-3">
                      <span className="font-bold text-foreground flex items-center gap-1.5">
                        <AudioLines size={13} className="text-purple-500" />
                        Listening CBT Controls
                      </span>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Play Limit</Label>
                        <select
                          className="w-full h-8 rounded-xl border border-border bg-background px-2 text-[11px] outline-none"
                          value={getPlayLimitFromUrl(activeScenario.audioUrl) || ""}
                          onChange={(e) => {
                            const newUrl = setPlayLimitInUrl(activeScenario.audioUrl, e.target.value);
                            updateScenarioMediaMutation.mutate({
                              scenarioId: activeScenario.id,
                              data: { audioUrl: newUrl }
                            });
                          }}
                        >
                          <option value="">Default (Exam Rules)</option>
                          <option value="1">1× (Official JLPT)</option>
                          <option value="2">2× (Official JFT)</option>
                          <option value="unlimited">Unlimited</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Questions under this scenario */}
                  {(() => {
                    const subQs = [
                      ...existingEditUi.filter(q => q.scenarioId === activeScenario.id),
                      ...draftQuestions.filter(q => q.scenarioId === activeScenario.id),
                    ].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

                    return (
                      <div className="space-y-2 pt-1">
                        <div className="flex items-center gap-2 border-t border-border/40 pt-4">
                          <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                            Questions in this Scenario ({subQs.length})
                          </Label>
                        </div>
                        {subQs.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-6 border border-dashed border-border/60 rounded-xl">
                            No questions yet. Click the left panel to add one.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {subQs.map((q, qIdx) => {
                              const isDraft = draftQuestions.some(d => d.id === q.id);
                              return (
                                <div
                                  key={q.id}
                                  className="p-3 rounded-xl border border-border/50 bg-background/60 space-y-2 cursor-pointer hover:border-primary/30 transition-all"
                                  onClick={() => setActiveItemId(q.id)}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                      {isDraft && (
                                        <span className="text-[8px] font-bold text-amber-500 bg-amber-500/10 px-1 py-0.5 rounded border border-amber-500/20">
                                          DRAFT
                                        </span>
                                      )}
                                      <span className="text-[10px] font-mono text-muted-foreground">Q{qIdx + 1}</span>
                                    </span>
                                    <span className="text-[10px] text-muted-foreground">{q.marks ?? 1} 点</span>
                                  </div>
                                  <p className="text-xs font-japanese text-foreground leading-snug line-clamp-2">
                                    {q.questionText || "(no question text)"}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <button
                          type="button"
                          className="w-full h-9 rounded-xl border border-dashed border-border/60 hover:border-primary/35 text-xs font-medium text-muted-foreground hover:text-primary transition-all flex items-center justify-center gap-1.5"
                          onClick={() => {
                            const d = emptyUiQuestion(activeSection === "ALL" ? "" : activeSection);
                            d.scenarioId = activeScenario.id;
                            const maxSort = Math.max(
                              ...draftQuestions.filter(q => q.scenarioId === activeScenario.id).map(q => q.sortOrder),
                              ...existingEditUi.filter(q => q.scenarioId === activeScenario.id).map(q => q.sortOrder),
                              -1
                            );
                            d.sortOrder = maxSort + 1;
                            setDraftQuestions(prev => [...prev, d]);
                            setActiveItemId(d.id);
                          }}
                        >
                          <Plus size={13} />
                          Add Question to this Scenario
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>

            ) : (
              /* ── Empty State ─────────────────────────────────────────── */
              <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 p-10 text-muted-foreground h-full">
                <div className="w-16 h-16 rounded-2xl bg-primary/5 border border-primary/10 flex items-center justify-center text-primary">
                  <Edit3 className="size-7" />
                </div>
                <div>
                  <h4 className="font-bold text-foreground text-sm">Authoring Workspace</h4>
                  <p className="text-xs max-w-xs mx-auto mt-1.5 leading-relaxed">
                    Select any question or scenario block from the left panel to begin editing.
                    Use the "Add Question" button to create new content.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT: CBT Live Preview ───────────────────────────────────── */}
          <div className="xl:col-span-4 flex flex-col bg-card border border-border/70 rounded-2xl overflow-hidden shadow-sm min-h-[600px] xl:min-h-0 xl:h-full">
            {/* Preview Header */}
            <div className="px-5 py-3.5 border-b border-border/50 flex items-center justify-between bg-card shrink-0">
              <h3 className="text-xs font-bold text-foreground flex items-center gap-1.5">
                <Eye className="size-3.5 text-emerald-500" />
                CBT Live Preview
              </h3>
              <span className="text-[9px] uppercase font-bold tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                Live Simulator
              </span>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin select-none flex flex-col bg-[#edece9]">
              {/* Mini Slate CBT Header */}
              <div className="bg-slate-900 border-b border-slate-800 text-slate-100 px-4 py-2.5 flex items-center justify-between text-[10px] font-sans font-bold shrink-0">
                <span>{activeConfig?.name === "JFT-Basic" ? "国際交流基金日本語基礎テスト (JFT-Basic) SIMULATION" : activeConfig?.name ? `${activeConfig.name} SIMULATION` : "日本語能力試験 (JLPT) SIMULATION"}</span>
                <span className="text-slate-400 font-mono">CBT SCREEN</span>
              </div>

              {activeScenario || (activeQuestion && activeQuestion.scenarioId) ? (
                /* ── Full Scenario Preview ─────────────────────────────── */
                <div className="p-4 space-y-4 flex-1">
                  {(() => {
                    const targetScenario = activeScenario || mockScenarios.find(s => s.id === activeQuestion?.scenarioId);
                    if (!targetScenario) return null;
                    const isEditing = activeScenario?.id === targetScenario.id;
                    const textToShow = isEditing && editingScenarioId === targetScenario.id
                      ? editingScenarioText
                      : (targetScenario.instructionsText ?? "");

                    return (
                      <>
                        {/* Scenario Passage Card */}
                        <div
                          className="bg-[#fcfaf6] border border-stone-200 shadow-sm rounded-xl overflow-hidden"

                        >
                          {/* Card Header */}
                          <div className="bg-stone-50 border-b border-stone-200 px-5 py-2 flex items-center justify-between">
                            <span className="text-[9px] uppercase font-bold tracking-widest text-stone-500 font-sans">
                              問題文 · Shared Context Passage
                            </span>
                            {isEditing && (
                              <span className="text-[8px] font-bold text-stone-600 bg-stone-200 px-1.5 py-0.5 rounded font-sans animate-pulse">
                                Editing
                              </span>
                            )}
                          </div>

                          <div className="p-5 space-y-4">
                            {textToShow ? (
                              <div className="text-[14px] leading-loose text-stone-900 whitespace-pre-wrap">
                                {renderFurigana(textToShow)}
                              </div>
                            ) : (
                              <p className="text-xs text-stone-400 italic font-sans py-4 text-center">
                                (No passage text entered yet)
                              </p>
                            )}
                            {targetScenario.imageUrl && (
                              <img
                                src={targetScenario.imageUrl}
                                alt=""
                                className="max-h-48 rounded-sm border border-stone-200 bg-white/40 object-contain w-full"
                              />
                            )}
                            {targetScenario.audioUrl && (
                              <div className="bg-stone-50 p-3 border border-stone-200 rounded-sm font-sans">
                                <p className="text-[9px] text-stone-500 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                                  <AudioLines size={10} /> CBT Audio Directive
                                </p>
                                <audio controls className="w-full h-8" src={targetScenario.audioUrl} />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Connected Questions */}
                        {(() => {
                          const targetId = activeScenario?.id || activeQuestion?.scenarioId;
                          const linked = [
                            ...existingEditUi.filter(q => q.scenarioId === targetId),
                            ...draftQuestions.filter(q => q.scenarioId === targetId),
                          ].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

                          return linked.map((q, idx) => {
                            const isEditingThis = activeQuestion?.id === q.id;
                            return (
                              <div
                                key={q.id}
                                className={`bg-white border shadow-sm rounded-xl overflow-hidden transition-all ${isEditingThis ? "border-slate-800 ring-1 ring-slate-800" : "border-stone-200"
                                  }`}

                              >
                                {/* Q Header */}
                                <div className="flex items-center justify-between px-5 py-2 border-b border-stone-200 bg-stone-50/60">
                                  <span className="text-[9px] uppercase font-bold tracking-widest text-stone-600 font-sans">
                                    問題 {idx + 1} · {q.examCategory}
                                  </span>
                                  <span className="text-[9px] font-sans text-stone-500">
                                    {q.marks ?? 1} 点
                                  </span>
                                </div>

                                <div className="p-5 space-y-4">
                                  <div className="text-[14px] leading-loose text-stone-900 font-medium whitespace-pre-wrap">
                                    {renderFurigana(q.questionText || "(Question text not entered...)")}
                                  </div>
                                  {q.imageUrl && (
                                    <img src={q.imageUrl} alt="" className="max-h-36 rounded-sm border border-stone-200 w-full object-contain" />
                                  )}
                                  {q.audioUrl && (
                                    <div className="bg-stone-50 p-2 border border-stone-200 rounded-sm font-sans">
                                      <audio controls className="w-full h-8" src={q.audioUrl} />
                                    </div>
                                  )}
                                  {/* MCQ Options */}
                                  {(() => {
                                    const qMeta = (q.options as { metadata?: any })?.metadata || {};
                                    const optionType = qMeta.optionType || "TEXT";

                                    if (optionType === "IMAGE") {
                                      return (
                                        <div className="grid grid-cols-2 gap-2 pt-1">
                                          {q.options.map((opt, oIdx) => {
                                            const isCorrect = q.correctId === opt.id;
                                            return (
                                              <div
                                                key={opt.id}
                                                className={cn(
                                                  "relative flex flex-col items-center justify-center rounded-xl border p-2.5 aspect-square bg-white shadow-xs",
                                                  isCorrect
                                                    ? "border-emerald-600 bg-emerald-50/15 text-stone-950 font-semibold ring-1 ring-emerald-500"
                                                    : "border-stone-200 text-stone-800"
                                                )}
                                              >
                                                <span className={cn(
                                                  "absolute top-2 left-2 text-sm font-bold select-none leading-none",
                                                  isCorrect ? "text-emerald-700" : "text-stone-300"
                                                )}>
                                                  {getCircleBadge(oIdx)}
                                                </span>
                                                {opt.imageUrl ? (
                                                  <img src={opt.imageUrl} alt="" className="max-h-[85%] max-w-[85%] object-contain rounded" />
                                                ) : (
                                                  <span className="text-[10px] text-muted-foreground italic">(No Image)</span>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    }

                                    if (optionType === "MIXED") {
                                      return (
                                        <div className="space-y-2">
                                          {q.options.map((opt, oIdx) => {
                                            const isCorrect = q.correctId === opt.id;
                                            return (
                                              <div
                                                key={opt.id}
                                                className={cn(
                                                  "flex items-start gap-3 px-4 py-2.5 border rounded-xl shadow-xs text-base bg-white",
                                                  isCorrect
                                                    ? "border-emerald-600 bg-emerald-50/15 text-stone-950 font-semibold ring-1 ring-emerald-500"
                                                    : "border-stone-200 text-stone-800"
                                                )}
                                              >
                                                <span className={cn(
                                                  "text-lg shrink-0 font-bold leading-none pt-0.5",
                                                  isCorrect ? "text-emerald-700" : "text-stone-300"
                                                )}>
                                                  {getCircleBadge(oIdx)}
                                                </span>
                                                <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                                                  <span className="text-[13px] block leading-snug">
                                                    {renderFurigana(opt.text || `Option ${getCircleBadge(oIdx)}`)}
                                                  </span>
                                                  {opt.imageUrl && (
                                                    <div className="w-16 h-12 shrink-0 border border-stone-200 rounded overflow-hidden bg-white flex items-center justify-center p-0.5">
                                                      <img src={opt.imageUrl} alt="" className="max-h-full max-w-full object-contain rounded" />
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    }

                                    // Default TEXT layout
                                    return (
                                      <div className="space-y-2">
                                        {q.options.map((opt, oIdx) => {
                                          const isCorrect = q.correctId === opt.id;
                                          return (
                                            <div
                                              key={opt.id}
                                              className={cn(
                                                "flex items-start gap-3 px-4 py-3 border rounded-xl transition-all duration-200 shadow-xs text-base bg-white",
                                                isCorrect
                                                  ? "border-emerald-650 bg-emerald-50/15 text-stone-950 font-semibold ring-1 ring-emerald-550"
                                                  : "border-stone-200 text-stone-800"
                                              )}
                                            >
                                              <span className={cn(
                                                "text-lg shrink-0 font-bold leading-none pt-0.5 transition-colors",
                                                isCorrect ? "text-slate-900" : "text-stone-300"
                                              )}>
                                                {getCircleBadge(oIdx)}
                                              </span>
                                              <div className="min-w-0 flex-1 pt-0.5">
                                                {(opt.text || !opt.imageUrl) && (
                                                  <span className="text-[13px] block leading-snug">
                                                    {renderFurigana(opt.text || `Option ${getCircleBadge(oIdx)}`)}
                                                  </span>
                                                )}
                                                {opt.imageUrl && (
                                                  <img src={opt.imageUrl} alt="" className="max-h-16 rounded-sm border border-stone-200 object-contain mt-1" />
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </>
                    );
                  })()}
                </div>
              ) : activeQuestion ? (
                /* ── Standalone Question Preview ───────────────────────── */
                <div className="p-4 space-y-4 flex-1">
                  <div
                    className="bg-white border border-stone-205 shadow-sm rounded-xl overflow-hidden"

                  >
                    {/* Q Header */}
                    <div className="flex items-center justify-between px-5 py-2 border-b border-stone-200 bg-stone-50/60">
                      <span className="text-[9px] uppercase font-bold tracking-widest text-stone-600 font-sans">
                        問題 · {activeQuestion.examCategory}
                      </span>
                      <span className="text-[9px] font-sans text-stone-500">
                        {activeQuestion.marks ?? 1} 点
                      </span>
                    </div>

                    <div className="p-5 space-y-4">
                      <div className="text-[14px] leading-loose text-stone-900 font-medium whitespace-pre-wrap">
                        {renderFurigana(activeQuestion.questionText || "(Input question stem text...)")}
                      </div>
                      {activeQuestion.imageUrl && (
                        <img src={activeQuestion.imageUrl} alt="" className="max-h-40 rounded-sm border border-stone-200 w-full object-contain" />
                      )}
                      {activeQuestion.audioUrl && (
                        <div className="bg-stone-50 p-2 border border-stone-200 rounded-sm font-sans">
                          <audio controls className="w-full h-8" src={activeQuestion.audioUrl} />
                        </div>
                      )}
                      {/* Options choices */}
                      {(() => {
                        const optionType = activeQuestion.optionType || "TEXT";

                        if (optionType === "IMAGE") {
                          return (
                            <div className="grid grid-cols-2 gap-2 pt-1 select-none">
                              {activeQuestion.options.map((opt, oIdx) => {
                                const isCorrect = activeQuestion.correctId === opt.id;
                                return (
                                  <div
                                    key={opt.id}
                                    className={cn(
                                      "relative flex flex-col items-center justify-center rounded-xl border p-2.5 aspect-square bg-white shadow-xs",
                                      isCorrect
                                        ? "border-emerald-600 bg-emerald-50/15 text-stone-950 font-semibold ring-1 ring-emerald-500"
                                        : "border-stone-200 text-stone-800"
                                    )}
                                  >
                                    <span className={cn(
                                      "absolute top-2 left-2 text-sm font-bold select-none leading-none",
                                      isCorrect ? "text-emerald-700" : "text-stone-300"
                                    )}>
                                      {getCircleBadge(oIdx)}
                                    </span>
                                    {opt.imageUrl ? (
                                      <img src={opt.imageUrl} alt="" className="max-h-[85%] max-w-[85%] object-contain rounded" />
                                    ) : (
                                      <span className="text-[10px] text-muted-foreground italic">(No Image)</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }

                        if (optionType === "MIXED") {
                          return (
                            <div className="space-y-2 select-none">
                              {activeQuestion.options.map((opt, oIdx) => {
                                const isCorrect = activeQuestion.correctId === opt.id;
                                return (
                                  <div
                                    key={opt.id}
                                    className={cn(
                                      "flex items-start gap-3 px-4 py-2.5 border rounded-xl shadow-xs text-base bg-white",
                                      isCorrect
                                        ? "border-emerald-600 bg-emerald-50/15 text-stone-950 font-semibold ring-1 ring-emerald-500"
                                        : "border-stone-200 text-stone-800"
                                    )}
                                  >
                                    <span className={cn(
                                      "text-lg shrink-0 font-bold leading-none pt-0.5",
                                      isCorrect ? "text-emerald-700" : "text-stone-300"
                                    )}>
                                      {getCircleBadge(oIdx)}
                                    </span>
                                    <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                                      <span className="text-[13px] block leading-snug">
                                        {renderFurigana(opt.text || `Option ${getCircleBadge(oIdx)}`)}
                                      </span>
                                      {opt.imageUrl && (
                                        <div className="w-16 h-12 shrink-0 border border-stone-200 rounded overflow-hidden bg-white flex items-center justify-center p-0.5">
                                          <img src={opt.imageUrl} alt="" className="max-h-full max-w-full object-contain rounded" />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }

                        // Default TEXT layout
                        return (
                          <div className="space-y-2 select-none">
                            {activeQuestion.options.map((opt, oIdx) => {
                              const isCorrect = activeQuestion.correctId === opt.id;
                              return (
                                <div
                                  key={opt.id}
                                  className={cn(
                                    "flex items-start gap-3 px-4 py-3 border rounded-xl transition-all duration-200 shadow-xs text-base bg-white",
                                    isCorrect
                                      ? "border-emerald-650 bg-emerald-50/15 text-stone-950 font-semibold ring-1 ring-emerald-550"
                                      : "border-stone-200 text-stone-800"
                                  )}
                                >
                                  <span className={cn(
                                    "text-lg shrink-0 font-bold leading-none pt-0.5 transition-colors",
                                    isCorrect ? "text-slate-900" : "text-stone-300"
                                  )}>
                                    {getCircleBadge(oIdx)}
                                  </span>
                                  <div className="min-w-0 flex-1 pt-0.5">
                                    {(opt.text || !opt.imageUrl) && (
                                      <span className="text-[13px] block leading-snug">
                                        {renderFurigana(opt.text || `Option ${getCircleBadge(oIdx)}`)}
                                      </span>
                                    )}
                                    {opt.imageUrl && (
                                      <img src={opt.imageUrl} alt="" className="max-h-16 rounded-sm border border-stone-200 object-contain mt-1" />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Preview Metadata Card */}
                  <div className="mt-4 p-4 rounded-xl bg-secondary/20 border border-border/40 space-y-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Question Metadata</p>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-muted-foreground block text-[10px]">Section</span>
                        <span className="font-semibold text-foreground">{activeQuestion.examCategory}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block text-[10px]">Marks</span>
                        <span className="font-semibold text-foreground">{activeQuestion.marks ?? 1} 点</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* ── Preview Empty State ───────────────────────────────── */
                <div className="flex flex-col items-center justify-center h-full py-20 gap-4 text-muted-foreground">
                  <div
                    className="w-36 h-48 rounded-sm border-2 border-stone-200 bg-white shadow-sm flex flex-col items-center justify-center gap-3 relative overflow-hidden"

                  >
                    <div className="absolute inset-[4px] border border-stone-100 opacity-50" />
                    <div className="space-y-2 w-full px-5">
                      {[70, 90, 60, 80].map((w, i) => (
                        <div key={i} className="h-1.5 bg-stone-100 rounded-full" style={{ width: `${w}%` }} />
                      ))}
                    </div>
                    <div className="space-y-1.5 w-full px-5">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-6 rounded-sm border border-stone-100 bg-stone-50/60" />
                      ))}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-semibold text-foreground">CBT Exam Preview</p>
                    <p className="text-[10px] mt-1 max-w-[180px] mx-auto">
                      Select a question or scenario to see the live exam simulation
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Delete Confirm Dialog ─────────────────────────────────────────── */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="max-w-sm" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              {mockScenarios.some(s => s.id === deleteConfirmId)
                ? "This will permanently delete the scenario block and all its nested questions. This action cannot be undone."
                : "This will permanently delete this question. This action cannot be undone."
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              size="sm"
              className="rounded-xl"
              onClick={() => {
                if (!deleteConfirmId) return;
                const isScenario = mockScenarios.some(s => s.id === deleteConfirmId);
                if (isScenario) {
                  deleteScenarioMutation.mutate(deleteConfirmId);
                } else {
                  const isDraft = draftQuestions.some(q => q.id === deleteConfirmId);
                  if (isDraft) {
                    setDraftQuestions(prev => prev.filter(q => q.id !== deleteConfirmId));
                    if (activeItemId === deleteConfirmId) setActiveItemId(null);
                    toast.success("Draft removed");
                  } else {
                    deleteQuestionMutation.mutate(deleteConfirmId);
                  }
                }
                setDeleteConfirmId(null);
              }}
            >
              {deleteScenarioMutation.isPending || deleteQuestionMutation.isPending ? (
                <Loader2 className="animate-spin size-3.5" />
              ) : (
                <Trash2 size={13} />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Draft Recovery Dialog ─────────────────────────────────────────── */}
      <Dialog open={showRecoveryDialog} onOpenChange={() => { }}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="text-amber-500 size-5" />
              Recover Unsaved Draft
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 mt-2">
                <div className="text-sm">
                  We found an unsaved draft for this mock test editor session from{" "}
                  <strong>{recoveredDraft ? new Date(recoveredDraft.timestamp).toLocaleString() : ""}</strong>.
                </div>
                <div className="text-xs text-muted-foreground bg-stone-50 p-2.5 rounded-lg border border-stone-150">
                  This includes draft questions, edited options, passages, and test configs that weren't published or saved.
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl border-dashed"
              onClick={handleDiscardDraft}
            >
              Discard Draft
            </Button>
            <Button
              variant="default"
              size="sm"
              className="rounded-xl bg-slate-900 text-white hover:bg-slate-800"
              onClick={handleRecoverDraft}
            >
              Recover Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Re-grade Dialog ────────────────────────────────────────────────── */}
      <Dialog open={showRegradeDialog} onOpenChange={(open) => { if (!regradeApplying) setShowRegradeDialog(open); }}>
        <DialogContent className="max-w-xl" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="text-amber-500 size-5" />
              Re-grade Submitted Attempts
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 mt-2">
                {regradeLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground py-6 justify-center">
                    <Loader2 className="animate-spin size-4" />
                    <span className="text-sm">Scanning all submitted attempts…</span>
                  </div>
                ) : regradePreview ? (
                  <>
                    <p className="text-sm text-foreground">
                      Scanned <strong>{regradePreview.total}</strong> submitted attempt{regradePreview.total !== 1 ? "s" : ""}.{" "}
                      <strong className={regradePreview.changed > 0 ? "text-amber-600" : "text-green-600"}>
                        {regradePreview.changed} will change.
                      </strong>
                    </p>

                    {regradePreview.changed === 0 ? (
                      <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3 text-xs text-green-700 dark:text-green-400">
                        ✅ All attempt scores are already correct. No changes needed.
                      </div>
                    ) : (
                      <>
                        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-2.5 text-[10px] text-amber-700 dark:text-amber-400">
                          ⚠ These attempts will be re-scored using the <strong>current correct answers</strong>. This will overwrite stored scores and pass/fail results.
                        </div>
                        <div className="max-h-52 overflow-y-auto rounded-lg border border-border divide-y divide-border text-xs">
                          {regradePreview.summary.map((row) => (
                            <div key={row.attemptId} className="px-3 py-2 flex items-center gap-3 bg-background hover:bg-muted/30 transition-colors">
                              <div className="flex-1 min-w-0">
                                <span className="font-mono text-muted-foreground text-[10px]">User #{row.userId}</span>
                                <span className="ml-2 text-[10px] text-muted-foreground">Attempt #{row.attemptNumber}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {row.scoreChanged && (
                                  <span className="text-[10px]">
                                    <span className="text-red-500 line-through">{row.oldScore}</span>
                                    <span className="mx-1 text-muted-foreground">→</span>
                                    <span className="text-green-600 font-bold">{row.newScore}</span>
                                    <span className="text-muted-foreground ml-0.5">pts</span>
                                  </span>
                                )}
                                {row.passChanged && (
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${row.newIsPassed
                                    ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                                    : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"
                                    }`}>
                                    {row.oldIsPassed === null ? "?" : row.oldIsPassed ? "PASS" : "FAIL"} → {row.newIsPassed ? "PASS" : "FAIL"}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : null}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl"
              disabled={regradeApplying}
              onClick={() => setShowRegradeDialog(false)}
            >
              Cancel
            </Button>
            {regradePreview && regradePreview.changed > 0 && (
              <Button
                size="sm"
                className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white"
                disabled={regradeApplying}
                onClick={async () => {
                  setRegradeApplying(true);
                  try {
                    const result = await regradeAttempts(mockTestId, false);
                    toast.success(`✅ Re-graded ${result.changed} attempt${result.changed !== 1 ? "s" : ""} successfully.`);
                    setShowRegradeDialog(false);
                    setRegradePreview(null);
                  } catch (e: any) {
                    toast.error(e?.message || "Re-grade failed");
                  } finally {
                    setRegradeApplying(false);
                  }
                }}
              >
                {regradeApplying
                  ? <Loader2 className="animate-spin size-3.5 mr-1" />
                  : <AlertCircle size={13} className="mr-1" />
                }
                Apply Re-grade ({regradePreview.changed} attempt{regradePreview.changed !== 1 ? "s" : ""})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
